import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback, createContext, useContext, forwardRef, useRef } from "react";
import { useMachine, useSelector } from "@xstate/react";
import { enqueueActions as enqueueActions$4, assign, createMachine, sendParent, fromPromise, sendTo, raise, stateIn, or, forwardTo, fromCallback } from "xstate";
import { useApolloClient } from "@apollo/client";
import clsx from "clsx";
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from "body-scroll-lock-upgrade";
import { createPortal } from "react-dom";
import './index.css';const LOCALSTORAGE = {
  RECENT_KEYWORDS: "sb-v1-recentKeywords",
  RECENT_LOCATIONS: "sb-v1-recentLocations"
};
const AUSTRALIA_TRENDING_SEARCHES = [
  { id: "889", label: "Plumbers", path: "plumbers" },
  { id: "135", label: "Dentists", path: "dentists" },
  { id: "246", label: "Hairdressers", path: "hairdressers" },
  { id: "161", label: "Electricians", path: "electricians" }
];
const COVERAGE_STATES = [
  { id: "qld", label: "QLD", path: "/qld" },
  { id: "nsw", label: "NSW", path: "/nsw" },
  { id: "vic", label: "VIC", path: "/vic" },
  { id: "nt", label: "NT", path: "/nt" }
];
const STATE_CODE_PATHS = {
  qld: "QLD",
  nsw: "NSW",
  nt: "NT",
  vic: "VIC",
  wa: "WA",
  tas: "TAS",
  act: "ACT",
  sa: "SA"
};
const EXPLICIT_CATEGORIES = {
  "1166": "Adult Shops",
  "319": "Lingerie & hoisery",
  "48231": "Adult Entertainment & Strippers",
  "713463": "Adult & Escort Services"
};
const EXPLICIT_CATEGORIES_ARRAY = Object.entries(EXPLICIT_CATEGORIES).flat();
const MOBILE_KEYWORD_PLACEHOLDER = "Search for a business or industry";
const MOBILE_LOCATION_PLACEHOLDER = "Enter your location";
const ELEMENTS = [
  "KEYWORD_FIELD_ID",
  "LOCATION_FIELD_ID",
  "SEARCH_BUTTON_ID",
  "OPEN_SEARCH_BUTTON_ID",
  "KEYWORD_SUGGESTION_BTN_CLASSNAME",
  "LOCATION_SUGGESTION_BTN_CLASSNAME",
  "SELECTABLE_SUGGESTION_BTN_CLASSNAME",
  "NAVIGABLE_SUGGESTION_BTN_CLASSNAME",
  "SUGGESTION_BTN_ID"
];
function useIsSsr() {
  const [isSsr, setIsSsr] = useState(true);
  useEffect(() => {
    setIsSsr(false);
  }, []);
  return isSsr;
}
function appendUTMParams(link2) {
  return link2;
}
function useImageResizer() {
  const isSsr = useIsSsr();
  const IMAGE_RESIZE_ROOT = "https://d2xzxktm3qm7rt.cloudfront.net";
  const supportsWebp = useMemo(() => {
    if (isSsr) {
      return false;
    }
    const elem = document.createElement("canvas");
    if (elem.getContext("2d")) {
      return elem.toDataURL("image/webp").includes("data:image/webp");
    }
    return false;
  }, [isSsr]);
  const resize = useCallback(
    (url = {}, { webp = supportsWebp, ...options } = {}) => {
      const imagePath = typeof url !== "string" ? url.s3_web_path || url.url : url;
      const webpFormat = webp ? { format: "webp" } : {};
      if (!imagePath) return "";
      const defaults = {
        fit: "inside",
        quality: 60,
        withoutEnlargement: true,
        ...webpFormat,
        ...options
      };
      const params = Object.keys(defaults).map((key) => `${key}_${String(defaults[key])}`).join(",");
      return `${String(IMAGE_RESIZE_ROOT)}/${params}/${imagePath}`;
    },
    [supportsWebp, IMAGE_RESIZE_ROOT]
  );
  return resize;
}
function highlightMatchingCharacters(inputValue, label2, matchOnlyFirst = true) {
  const regex = new RegExp(`(${inputValue})`, "gi");
  const parts = label2.split(regex);
  let matched = false;
  return parts.map((part, index) => {
    if (!matched && part.toLowerCase() === inputValue.toLowerCase()) {
      if (matchOnlyFirst) {
        matched = true;
      }
      return /* @__PURE__ */ jsx("strong", { children: part }, `${part}-${String(index)}`);
    }
    return part;
  });
}
function updateRecentSearchesInLocalStorage(newSearch, type, category) {
  const existingSearches = window.localStorage.getItem(
    LOCALSTORAGE[category === "keyword" ? "RECENT_KEYWORDS" : "RECENT_LOCATIONS"]
  );
  const parsedExistingSearches = existingSearches ? JSON.parse(existingSearches) : void 0;
  if (!newSearch.id || !newSearch.label) {
    return parsedExistingSearches || [];
  }
  const updatedRecentSearches = [
    {
      type,
      ...newSearch
    },
    ...(parsedExistingSearches == null ? void 0 : parsedExistingSearches.length) ? parsedExistingSearches.filter(
      ({ id, label: label2 }) => !!id && !!label2 && id !== newSearch.id && (!label2 || label2.toLowerCase() !== newSearch.label.toLowerCase())
    ) : []
  ];
  window.localStorage.setItem(
    LOCALSTORAGE[category === "keyword" ? "RECENT_KEYWORDS" : "RECENT_LOCATIONS"],
    JSON.stringify(updatedRecentSearches)
  );
  return updatedRecentSearches;
}
const focusElement = (_, { id }) => {
  var _a;
  (_a = document.getElementById(id)) == null ? void 0 : _a.focus();
};
const navigateElements = (_, { direction, classname }) => {
  const allElements = document.getElementsByClassName(classname);
  const currentElementIndex = Array.from(allElements).findIndex(
    (element) => element === document.activeElement
  );
  const targetElement = allElements[currentElementIndex + (direction === "next" ? 1 : -1)];
  if (targetElement) {
    targetElement.setAttribute("tabindex", "-1");
    targetElement.focus();
  }
};
const setInputToSuggestion = ({ event }, params) => {
  const suggestion = params.suggestion || event.suggestion;
  const inputElement = document.getElementById(params.inputId);
  if (inputElement && ((suggestion == null ? void 0 : suggestion.inputValue) || (suggestion == null ? void 0 : suggestion.label))) {
    inputElement.value = suggestion.inputValue || suggestion.label;
  }
};
const revertInput = ({ context }, { inputId }) => {
  const inputElement = document.getElementById(inputId);
  if (inputElement) {
    inputElement.value = context.input;
  }
};
function enqueueActions$3(params) {
  return enqueueActions$4(params);
}
function useCreateMapProfileSuggestions() {
  const resize = useImageResizer();
  const mapProfileSuggestions = enqueueActions$3(
    ({ context, event, enqueue }) => {
      var _a, _b;
      enqueue(
        assign({
          profileSuggestions: ((_b = (_a = event.data.profileSuggest) == null ? void 0 : _a.edges) == null ? void 0 : _b.length) ? event.data.profileSuggest.edges.reduce((agg, edge) => {
            var _a2;
            const { node } = edge || {};
            if (node == null ? void 0 : node.vanityUrlPath) {
              agg.push({
                id: node.id,
                label: node.displayName,
                labelParts: highlightMatchingCharacters(context.input, node.displayName),
                path: `/profile/${node.vanityUrlPath}`,
                logo: node.logo ? resize(node.logo, { height: 34, width: 34 }) : void 0,
                address: `${node.suburb}, ${node.stateCode}`,
                packageId: ((_a2 = node.package) == null ? void 0 : _a2.id) || null
              });
            }
            return agg;
          }, []) : []
        })
      );
    }
  );
  return mapProfileSuggestions;
}
const mapCategorySuggestions = enqueueActions$3(
  ({ context, event, enqueue }) => {
    var _a, _b;
    enqueue(
      assign({
        categorySuggestions: ((_b = (_a = event.data.categorySuggest) == null ? void 0 : _a.edges) == null ? void 0 : _b.length) ? event.data.categorySuggest.edges.reduce((agg, edge) => {
          const { node } = edge || {};
          if (node) {
            agg.push({
              id: node.id,
              label: node.name,
              labelParts: highlightMatchingCharacters(context.input, node.name),
              path: node.path
            });
          }
          return agg;
        }, []) : []
      })
    );
  }
);
const mapTrendingSearches = enqueueActions$3(
  ({ event, enqueue }) => {
    enqueue(
      assign({
        trendingSearches: event.data.length ? event.data.slice(0, 4).reduce((agg, edge) => {
          const { node } = edge || {};
          if (node) {
            agg.push({ id: node.id, label: node.name, path: node.path });
          }
          return agg;
        }, []).slice(0, 4) : []
      })
    );
  }
);
const assignAutoComplete$1 = enqueueActions$3(({ context, enqueue }) => {
  var _a;
  const { input, categorySuggestions } = context;
  const topCategorySuggestion = categorySuggestions[0];
  if (!input.length || !topCategorySuggestion) {
    enqueue(
      assign({
        autoComplete: {
          input,
          autocomplete: void 0,
          suggestion: null
        }
      })
    );
    return;
  }
  const commonPrefixPattern = new RegExp(`^(${input})`, "i");
  const commonPrefixMatch = topCategorySuggestion.label.match(commonPrefixPattern);
  const autocomplete2 = ((_a = commonPrefixMatch == null ? void 0 : commonPrefixMatch[1]) == null ? void 0 : _a.length) ? topCategorySuggestion.label.slice(commonPrefixMatch[1].length) : void 0;
  const suggestion = autocomplete2 || input.length && input.toLowerCase() === topCategorySuggestion.label.toLowerCase() ? topCategorySuggestion : null;
  enqueue(
    assign({
      autoComplete: {
        input,
        autocomplete: autocomplete2,
        suggestion
      }
    })
  );
});
const addInitialCategoryToRecentKeywordSearches = enqueueActions$3(
  ({ event, enqueue }) => {
    const updatedRecentKeywords = updateRecentSearchesInLocalStorage(
      event.initialCategory,
      "selectable",
      "keyword"
    );
    enqueue(assign({ recentKeywordSearches: updatedRecentKeywords }));
  }
);
const addInitialProfileToRecentKeywordSearches = enqueueActions$3(
  ({ event, enqueue }) => {
    const updatedRecentKeywords = updateRecentSearchesInLocalStorage(
      event.initialProfile,
      "navigable",
      "keyword"
    );
    enqueue(assign({ recentKeywordSearches: updatedRecentKeywords }));
  }
);
const clearAllRecentKeywordSearches = enqueueActions$3(({ enqueue }) => {
  window.localStorage.removeItem(LOCALSTORAGE.RECENT_KEYWORDS);
  enqueue(assign({ recentKeywordSearches: [] }));
});
const deleteRecentKeywordSearch = enqueueActions$3(
  ({ context, event, enqueue }) => {
    const filteredRecentSearches = context.recentKeywordSearches.filter(
      ({ id }) => id !== event.recentSearch.id
    );
    if (filteredRecentSearches.length) {
      window.localStorage.setItem(
        LOCALSTORAGE.RECENT_KEYWORDS,
        JSON.stringify(filteredRecentSearches)
      );
    } else {
      window.localStorage.removeItem(LOCALSTORAGE.RECENT_KEYWORDS);
    }
    enqueue(assign({ recentKeywordSearches: filteredRecentSearches }));
  }
);
const keywordDialogActorActions = {
  mapCategorySuggestions,
  mapTrendingSearches,
  navigateElements,
  setInputToSuggestion,
  revertInput,
  focusElement,
  assignAutoComplete: assignAutoComplete$1,
  addInitialCategoryToRecentKeywordSearches,
  addInitialProfileToRecentKeywordSearches,
  clearAllRecentKeywordSearches,
  deleteRecentKeywordSearch
};
const KeywordSuggestDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "keywordSuggest" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "query" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "first" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Int" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "statisticalAreaIds" } }, "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "enabled" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "profileSuggest" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "query" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "query" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "first" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "first" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "statisticalAreaIds" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "statisticalAreaIds" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "edges" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "node" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "displayName" } }, { "kind": "Field", "name": { "kind": "Name", "value": "logo" } }, { "kind": "Field", "name": { "kind": "Name", "value": "vanityUrlPath" } }, { "kind": "Field", "name": { "kind": "Name", "value": "suburb" } }, { "kind": "Field", "name": { "kind": "Name", "value": "stateCode" } }, { "kind": "Field", "name": { "kind": "Name", "value": "package" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] } }] } }, { "kind": "Field", "name": { "kind": "Name", "value": "categorySuggest" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "query" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "query" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "first" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "first" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "enabled" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "enabled" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "edges" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "node" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "path" } }] } }] } }] } }] } }] };
const LocationSuggestDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "locationSuggest" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "query" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "first" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Int" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "enabled" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "searchAreaSuggest" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "query" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "query" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "first" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "first" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "enabled" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "enabled" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "edges" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "node" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "path" } }, { "kind": "Field", "name": { "kind": "Name", "value": "statisticalAreas" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] } }] } }] } }] };
const NearbyLocationsDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "nearbyLocations" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "first" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Int" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "enabled" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "coordinates" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "searchAreaSearch" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "first" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "first" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "coordinates" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "coordinates" } } }, { "kind": "Argument", "name": { "kind": "Name", "value": "enabled" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "enabled" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "edges" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "node" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "description" } }, { "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "name" } }, { "kind": "Field", "name": { "kind": "Name", "value": "path" } }, { "kind": "Field", "name": { "kind": "Name", "value": "statisticalAreas" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] } }] } }] } }] };
const TrendingSearchesDocument = { "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "trendingSearches" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "params" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "JSONObject" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "searchSeoHome" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "params" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "params" } } }] }] } }] };
const documents = {
  "\n  query keywordSuggest(\n    $query: String!\n    $first: Int!\n    $statisticalAreaIds: [String!]\n    $enabled: Boolean\n  ) {\n    profileSuggest(query: $query, first: $first, statisticalAreaIds: $statisticalAreaIds) {\n      edges {\n        node {\n          id\n          displayName\n          logo\n          vanityUrlPath\n          suburb\n          stateCode\n          package {\n            id\n          }\n        }\n      }\n    }\n    categorySuggest(query: $query, first: $first, enabled: $enabled) {\n      edges {\n        node {\n          id\n          name\n          path\n        }\n      }\n    }\n  }\n": KeywordSuggestDocument,
  "\n  query locationSuggest($query: String!, $first: Int!, $enabled: Boolean) {\n    searchAreaSuggest(query: $query, first: $first, enabled: $enabled) {\n      edges {\n        node {\n          description\n          id\n          name\n          path\n          statisticalAreas {\n            id\n          }\n        }\n      }\n    }\n  }\n": LocationSuggestDocument,
  "\n  query nearbyLocations($first: Int!, $enabled: Boolean, $coordinates: String) {\n    searchAreaSearch(first: $first, coordinates: $coordinates, enabled: $enabled) {\n      edges {\n        node {\n          description\n          id\n          name\n          path\n          statisticalAreas {\n            id\n          }\n        }\n      }\n    }\n  }\n": NearbyLocationsDocument,
  "\n  query trendingSearches($params: JSONObject) {\n    searchSeoHome(params: $params)\n  }\n": TrendingSearchesDocument
};
function gql(source) {
  var _a;
  return (_a = documents[source]) != null ? _a : {};
}
const keywordSuggestQuery = gql(`
  query keywordSuggest(
    $query: String!
    $first: Int!
    $statisticalAreaIds: [String!]
    $enabled: Boolean
  ) {
    profileSuggest(query: $query, first: $first, statisticalAreaIds: $statisticalAreaIds) {
      edges {
        node {
          id
          displayName
          logo
          vanityUrlPath
          suburb
          stateCode
          package {
            id
          }
        }
      }
    }
    categorySuggest(query: $query, first: $first, enabled: $enabled) {
      edges {
        node {
          id
          name
          path
        }
      }
    }
  }
`);
function useKeywordSuggest() {
  const client = useApolloClient();
  async function fetchKeywordSuggestions({
    query,
    selectedSearchArea
  }) {
    return client.query({
      query: keywordSuggestQuery,
      variables: {
        first: 4,
        enabled: true,
        query: query || "",
        ...selectedSearchArea ? { statisticalAreaIds: selectedSearchArea.statisticalAreas.map(({ id }) => id) } : {}
      }
    }).then((res) => res.data);
  }
  return fetchKeywordSuggestions;
}
function capitalize(text2) {
  if (!text2) return "";
  return text2.replace(
    /\w\S*/g,
    (txt) => `${txt.charAt(0).toUpperCase()}${txt.slice(1).toLowerCase()}`
  );
}
function deparameterize(slug) {
  if (!slug) return "";
  return slug.replace(/-/g, " ");
}
function deconstructLocation(address2) {
  var _a, _b, _c, _d;
  if (!address2) {
    return { location: address2 };
  }
  const cleanedLocation = decodeURIComponent(address2).replace(/,/g, ", ").replace(/[^0-9a-z,-]/gi, " ").replace(/[,\s]+|[,\s]+/g, " ").trim();
  const postcode = (_b = (_a = cleanedLocation.match(/(\d{4})$/g)) == null ? void 0 : _a[0]) == null ? void 0 : _b.trim();
  const state = (_d = (_c = cleanedLocation.match(/\b(QLD|NT|NSW|VIC|SA|ACT|WA)\b(?=(?:[,-\s]*\s*\d{4})?$)/i)) == null ? void 0 : _c[0]) == null ? void 0 : _d.trim().toUpperCase();
  const searchAreaName = capitalize(
    deparameterize(
      cleanedLocation.replace(
        /(\bQLD\b|\bNT\b|\bNSW\b|\bVIC\b|\bSA\b|\bACT\b|\bWA\b|\b\d{4}\b|region|online)/gi,
        ""
      )
    )
  ).trim();
  if (!state) {
    return { location: address2 };
  }
  const location = [searchAreaName, state, postcode].filter(Boolean).join(", ");
  const parts = { location, searchAreaName, state, postcode };
  return parts;
}
function slugify(str, customRegex = "", customRegexReplace = "") {
  if (!str || typeof str !== "string") return "";
  return str.trim().toLowerCase().replace(customRegex, customRegexReplace).replace(/[“”‘’]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").trim();
}
const trendingSearchesQuery = gql(`
  query trendingSearches($params: JSONObject) {
    searchSeoHome(params: $params)
  }
`);
function useTrendingSearches() {
  const client = useApolloClient();
  async function fetchTrendingSearches({ selectedSearchArea }) {
    const { searchAreaName, state } = deconstructLocation(
      (selectedSearchArea == null ? void 0 : selectedSearchArea.inputValue) || (selectedSearchArea == null ? void 0 : selectedSearchArea.label)
    );
    function isValid(data) {
      return !!data && typeof data === "object" && "data" in data && Array.isArray(data.data) && !!data.data[0];
    }
    if (!searchAreaName || !state) {
      return [];
    }
    return client.query({
      query: trendingSearchesQuery,
      variables: {
        params: {
          suburb: searchAreaName,
          state
        }
      }
    }).then((res) => {
      var _a;
      const result = res;
      if (!isValid(result)) {
        return [];
      }
      const { categories } = ((_a = result.searchSeoHome.data) == null ? void 0 : _a[0].attributes) || {};
      if (!categories) {
        return [];
      }
      const formattedAsSearchArea = categories.reduce(
        (agg, { category_id, name }) => {
          if (!EXPLICIT_CATEGORIES_ARRAY.includes(category_id) && !EXPLICIT_CATEGORIES_ARRAY.includes(name)) {
            agg.push({
              node: {
                id: category_id || name,
                name,
                path: slugify(name)
              }
            });
          }
          return agg;
        },
        []
      );
      return formattedAsSearchArea;
    });
  }
  return fetchTrendingSearches;
}
const searchSuggestionsActor = createMachine({
  description: "Load and return search suggestions from the server using a debouncer",
  id: "searchSuggestionsActor",
  types: {},
  context: {
    variables: {},
    callbackPrefix: "FETCH"
  },
  on: {
    "FETCH.SET_CALLBACK_PREFIX": {
      actions: assign({ callbackPrefix: ({ event }) => event.callbackPrefix })
    },
    "FETCH.DEBOUNCE": {
      target: ".debouncing",
      reenter: true
    },
    "FETCH.FETCH": ".fetching"
  },
  initial: "idle",
  states: {
    hist: { type: "history" },
    idle: { description: "Waiting to be invoked" },
    debouncing: {
      description: "Waiting for user to stop typing",
      entry: assign({
        variables: ({ event }) => "variables" in event ? event.variables : {}
      }),
      after: { 500: "fetching" }
    },
    fetching: {
      description: "Fetching suggestions from the server",
      entry: sendParent(({ context }) => ({ type: `${context.callbackPrefix}.FETCHING` })),
      invoke: {
        src: "fetchActor",
        input: ({ context, event }) => "variables" in event ? event.variables : context.variables,
        onDone: {
          actions: sendParent(({ event, context }) => ({
            type: `${context.callbackPrefix}.RESOLVED`,
            data: event.output
          })),
          target: "idle"
        },
        onError: {
          actions: sendParent(({ event, context }) => ({
            type: `${context.callbackPrefix}.REJECTED`,
            error: event.error
          })),
          target: "idle"
        }
      }
    }
  }
});
function useCreateFetchKeywordSuggestionsActor() {
  const fetchKeywordSuggestions = useKeywordSuggest();
  return searchSuggestionsActor.provide({
    actors: {
      fetchActor: fromPromise(
        ({ input }) => fetchKeywordSuggestions(input)
      )
    }
  });
}
function useCreateFetchTrendingSearchesActor() {
  const fetchTrendingSearches = useTrendingSearches();
  return searchSuggestionsActor.provide({
    actors: {
      fetchActor: fromPromise(
        ({ input }) => fetchTrendingSearches(input)
      )
    }
  });
}
function useCreateKeywordDialogActorActors() {
  const fetchKeywordSuggestionsActor = useCreateFetchKeywordSuggestionsActor();
  const fetchTrendingSearchesActor = useCreateFetchTrendingSearchesActor();
  return {
    fetchKeywordSuggestionsActor,
    fetchTrendingSearchesActor
  };
}
function createElementIdentifiers(elementPrefix) {
  return ELEMENTS.reduce(
    (agg, identifier) => ({
      ...agg,
      [identifier]: `${elementPrefix}-${identifier}`
    }),
    // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter
    {}
  );
}
const keywordDialogActorContext = {
  input: "",
  autoComplete: { input: "", suggestion: null },
  categorySuggestions: [],
  profileSuggestions: [],
  trendingSearches: [],
  recentKeywordSearches: [],
  selectedCategory: null,
  selectedProfile: null,
  fetchKeywordSuggestionsActor: void 0,
  fetchTrendingSearchesActor: void 0,
  elementIdentifiers: createElementIdentifiers("")
};
const hasPreviousElements = (_, { classname }) => {
  if (!classname) {
    return false;
  }
  const allElements = document.getElementsByClassName(classname);
  const currentElementIndex = Array.from(allElements).findIndex(
    (element) => element === document.activeElement
  );
  const targetElement = allElements[currentElementIndex - 1];
  if (!(targetElement == null ? void 0 : targetElement.classList.length)) {
    return false;
  }
  return !!targetElement.classList.contains(classname);
};
const hasProfileSuggestions = ({
  event
}) => {
  var _a, _b;
  return !!((_b = (_a = event.data.profileSuggest) == null ? void 0 : _a.edges) == null ? void 0 : _b.length);
};
const hasCategorySuggestions = ({
  event
}) => {
  var _a, _b;
  return !!((_b = (_a = event.data.categorySuggest) == null ? void 0 : _a.edges) == null ? void 0 : _b.length);
};
const keywordDialogActorGuards = {
  hasCategorySuggestions,
  hasProfileSuggestions,
  hasPreviousElements
};
const keywordDialogActor = createMachine({
  id: "keywordDialogActor",
  types: {},
  context: ({ spawn }) => ({
    ...keywordDialogActorContext,
    fetchKeywordSuggestionsActor: spawn("fetchKeywordSuggestionsActor"),
    fetchTrendingSearchesActor: spawn("fetchTrendingSearchesActor")
  }),
  entry: [
    sendTo(({ context }) => context.fetchKeywordSuggestionsActor, {
      type: "FETCH.SET_CALLBACK_PREFIX",
      callbackPrefix: "FETCH_KEYWORD_SUGGESTIONS"
    }),
    sendTo(({ context }) => context.fetchTrendingSearchesActor, {
      type: "FETCH.SET_CALLBACK_PREFIX",
      callbackPrefix: "FETCH_TRENDING_SEARCHES"
    })
  ],
  on: {
    "LOCATION_INPUT.FOCUS": {
      target: [".confirmedSearchArea.confirmedSearchArea", ".suggestions.hist"]
    },
    "INTERNAL.SEARCH": [
      {
        guard: ({ event }) => !!event.value.length,
        actions: [
          assign({ input: ({ event }) => event.value, selectedCategory: null }),
          "assignAutoComplete",
          sendTo(
            ({ context }) => context.fetchKeywordSuggestionsActor,
            ({ event, system }) => ({
              type: "FETCH.DEBOUNCE",
              variables: {
                query: event.value,
                selectedSearchArea: system.get("locationDialogActor").getSnapshot().context.selectedSearchArea
              }
            })
          )
        ]
      },
      {
        actions: assign({
          input: keywordDialogActorContext.input,
          selectedCategory: keywordDialogActorContext.selectedCategory,
          autoComplete: keywordDialogActorContext.autoComplete
        }),
        target: [".suggestions.empty", ".confirmedSearchArea.hist"]
      }
    ],
    "INTERNAL.SELECT_AUTOCOMPLETE": {
      guard: ({ context }) => !!context.autoComplete.suggestion,
      actions: [
        assign({
          selectedCategory: ({ context }) => context.autoComplete.suggestion,
          autoComplete: keywordDialogActorContext.autoComplete
        }),
        {
          type: "setInputToSuggestion",
          params: ({ context }) => ({
            inputId: context.elementIdentifiers.KEYWORD_FIELD_ID,
            suggestion: context.selectedCategory
          })
        }
      ]
    },
    "INTERNAL.SELECT_CATEGORY_SUGGESTION": {
      actions: [
        assign({
          autoComplete: keywordDialogActorContext.autoComplete,
          selectedCategory: ({ event }) => event.suggestion,
          selectedProfile: null
        }),
        sendTo(({ system }) => system.get("locationDialogActor"), {
          type: "CATEGORY.CONFIRM"
        }),
        sendParent({ type: "SEARCH.ATTEMPT", trigger: "suggestionButtonClick" }),
        {
          type: "setInputToSuggestion",
          params: ({ context, event }) => ({
            suggestion: event.suggestion,
            inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
          })
        }
      ]
    },
    "INTERNAL.SELECT_PROFILE_SUGGESTION": {
      actions: [
        assign({
          autoComplete: keywordDialogActorContext.autoComplete,
          selectedProfile: ({ event }) => event.suggestion,
          selectedCategory: null
        }),
        sendParent({ type: "SEARCH.ATTEMPT", trigger: "suggestionButtonClick" }),
        {
          type: "setInputToSuggestion",
          params: ({ event, context }) => ({
            suggestion: event.suggestion,
            inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
          })
        }
      ]
    },
    "RECENT_KEYWORD_SEARCHES.SET": {
      actions: assign({ recentKeywordSearches: ({ event }) => event.recentKeywordSearches })
    },
    "INITIAL_CATEGORY.SET": {
      guard: ({ event }) => !!event.initialCategory,
      actions: "addInitialCategoryToRecentKeywordSearches"
    },
    "INITIAL_PROFILE.SET": {
      guard: ({ event }) => !!event.initialProfile,
      actions: "addInitialProfileToRecentKeywordSearches"
    },
    "ELEMENT_IDENTIFIERS.SET": {
      actions: assign({ elementIdentifiers: ({ event }) => event.elementIdentifiers })
    },
    "INPUT.CHANGED": {
      actions: raise(({ event }) => ({ type: "INTERNAL.SEARCH", value: event.value }))
    },
    "FETCH_KEYWORD_SUGGESTIONS.FETCHING": {
      guard: ({ context }) => !!context.input.length,
      target: [".suggestions.loading", ".confirmedSearchArea.hist"]
    },
    "FETCH_TRENDING_SEARCHES.RESOLVED": { actions: "mapTrendingSearches" },
    "KEYWORD_DIALOG.REFETCH": [
      {
        guard: stateIn("resolved.withSuggestions"),
        actions: [
          sendTo(
            ({ context }) => context.fetchKeywordSuggestionsActor,
            ({ context, event }) => ({
              type: "FETCH.FETCH",
              variables: {
                query: context.input,
                selectedSearchArea: event.selectedSearchArea
              }
            })
          ),
          sendTo(
            ({ context }) => context.fetchTrendingSearchesActor,
            ({ event }) => ({ type: "FETCH.FETCH", variables: event })
          )
        ],
        target: [".suggestions.loading", ".confirmedSearchArea.hist"]
      },
      {
        actions: sendTo(
          ({ context }) => context.fetchTrendingSearchesActor,
          ({ event }) => ({ type: "FETCH.FETCH", variables: event })
        )
      }
    ],
    "KEY.ESCAPE": {
      actions: [
        assign({ selectedCategory: null }),
        {
          type: "revertInput",
          params: ({ context }) => ({ inputId: context.elementIdentifiers.KEYWORD_FIELD_ID })
        },
        {
          type: "focusElement",
          params: ({ context }) => ({ id: context.elementIdentifiers.KEYWORD_FIELD_ID })
        }
      ]
    },
    "KEY.ARROW_DOWN": {
      actions: {
        type: "navigateElements",
        params: ({ context }) => ({
          direction: "next",
          classname: context.elementIdentifiers.KEYWORD_SUGGESTION_BTN_CLASSNAME
        })
      }
    },
    "KEY.ARROW_UP": [
      {
        guard: {
          type: "hasPreviousElements",
          params: ({ context }) => ({
            classname: context.elementIdentifiers.KEYWORD_SUGGESTION_BTN_CLASSNAME
          })
        },
        actions: {
          type: "navigateElements",
          params: ({ context }) => ({
            direction: "previous",
            classname: context.elementIdentifiers.KEYWORD_SUGGESTION_BTN_CLASSNAME
          })
        }
      },
      { actions: raise({ type: "KEY.ESCAPE" }) }
    ],
    "SELECTABLE_SUGGESTION_BTN.FOCUS": {
      actions: [
        assign({ selectedCategory: ({ event }) => event.suggestion, selectedProfile: null }),
        {
          type: "setInputToSuggestion",
          params: ({ event, context }) => ({
            suggestion: event.suggestion,
            inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
          })
        }
      ]
    },
    "NAVIGABLE_SUGGESTION_BTN.FOCUS": {
      actions: [
        assign({ selectedProfile: ({ event }) => event.suggestion, selectedCategory: null }),
        {
          type: "setInputToSuggestion",
          params: ({ event, context }) => ({
            suggestion: event.suggestion,
            inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
          })
        }
      ]
    },
    "NAVIGABLE_SUGGESTION_BTN.CLICK": {
      actions: [
        assign({
          input: ({ event }) => event.suggestion.label
        }),
        raise(({ event }) => ({
          type: "INTERNAL.SELECT_PROFILE_SUGGESTION",
          suggestion: event.suggestion
        }))
      ]
    }
  },
  type: "parallel",
  states: {
    confirmedSearchArea: {
      initial: "unconfirmedSearchArea",
      states: {
        hist: { type: "history" },
        unconfirmedSearchArea: {
          on: {
            "SEARCH_AREA.CONFIRM": {
              target: ["confirmedSearchArea", "#keywordDialogActor.suggestions.hist"]
            },
            "MOBILE_KEY.ENTER": {
              actions: [
                sendTo(({ system }) => system.get("locationDialogActor"), {
                  type: "CATEGORY.CONFIRM"
                }),
                {
                  type: "focusElement",
                  params: ({ context }) => ({
                    id: context.elementIdentifiers.LOCATION_FIELD_ID
                  })
                }
              ]
            },
            "SELECTABLE_SUGGESTION_BTN.CLICK": {
              actions: [
                assign({
                  input: ({ event }) => event.suggestion.label,
                  selectedCategory: ({ event }) => event.suggestion,
                  selectedProfile: null
                }),
                sendTo(({ system }) => system.get("locationDialogActor"), {
                  type: "CATEGORY.CONFIRM"
                }),
                {
                  type: "setInputToSuggestion",
                  params: ({ event, context }) => ({
                    suggestion: event.suggestion,
                    inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
                  })
                },
                {
                  type: "focusElement",
                  params: ({ context }) => ({
                    id: context.elementIdentifiers.LOCATION_FIELD_ID
                  })
                }
              ]
            }
          }
        },
        confirmedSearchArea: {
          on: {
            "SEARCH_AREA.UNCONFIRM": "unconfirmedSearchArea",
            "MOBILE_KEY.ENTER": {
              actions: [
                sendTo(({ system }) => system.get("locationDialogActor"), {
                  type: "CATEGORY.CONFIRM"
                }),
                sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
              ]
            },
            "SELECTABLE_SUGGESTION_BTN.CLICK": {
              actions: [
                raise(({ event }) => ({
                  type: "INTERNAL.SELECT_CATEGORY_SUGGESTION",
                  suggestion: event.suggestion
                })),
                assign({
                  input: ({ event }) => event.suggestion.label
                })
              ]
            }
          }
        }
      }
    },
    suggestions: {
      initial: "empty",
      states: {
        hist: { type: "history", history: "deep" },
        empty: {
          on: {
            "KEY.ENTER": { actions: sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" }) },
            "CLEAR_ALL_RECENT_SEARCHES_BTN.CLICK": { actions: "clearAllRecentKeywordSearches" },
            "DELETE_RECENT_SEARCH_BTN.CLICK": { actions: "deleteRecentKeywordSearch" },
            "INITIAL_CATEGORY.SET": {
              guard: ({ event }) => !!event.initialCategory,
              actions: [
                {
                  type: "setInputToSuggestion",
                  params: ({ context, event }) => ({
                    suggestion: event.initialCategory,
                    inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
                  })
                },
                "addInitialCategoryToRecentKeywordSearches",
                assign({
                  selectedCategory: ({ event }) => event.initialCategory,
                  input: ({ event }) => event.initialCategory.label
                })
              ]
            },
            "INITIAL_PROFILE.SET": {
              guard: ({ event }) => !!event.initialProfile,
              actions: [
                {
                  type: "setInputToSuggestion",
                  params: ({ context, event }) => ({
                    suggestion: event.initialProfile.category,
                    inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
                  })
                },
                "addInitialProfileToRecentKeywordSearches",
                assign({
                  selectedCategory: ({ event }) => event.initialProfile.category,
                  input: ({ event }) => event.initialProfile.category.label
                })
              ]
            },
            "RECENT_KEYWORD_SEARCHES.SET": [
              {
                description: "If no initial category or profile has been provided then set the initial keyword to the most recently searched category",
                guard: ({ context }) => !context.selectedCategory && !context.selectedProfile,
                actions: [
                  assign({
                    recentKeywordSearches: ({ event }) => event.recentKeywordSearches,
                    selectedCategory: ({ event }) => event.recentKeywordSearches.find(({ type }) => type === "selectable") || null,
                    input: ({ event }) => {
                      var _a;
                      return ((_a = event.recentKeywordSearches[0]) == null ? void 0 : _a.label) || "";
                    }
                  }),
                  {
                    type: "setInputToSuggestion",
                    params: ({ event, context }) => ({
                      suggestion: event.recentKeywordSearches.find(
                        ({ type }) => type === "selectable"
                      ),
                      inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
                    })
                  }
                ]
              },
              {
                actions: assign({
                  recentKeywordSearches: ({ event }) => event.recentKeywordSearches
                })
              }
            ],
            "SELECTABLE_SUGGESTION_BTN.CLICK": [
              {
                guard: ({ event }) => event.identifier === "trendingSearch",
                actions: sendParent(({ event }) => ({
                  type: "PAGE_DATA_LAYER.PUSH",
                  event: "Site Search-Trending Search List Clicked",
                  search_term: event.suggestion.label
                }))
              },
              {
                guard: ({ event }) => event.identifier === "recentSearch",
                actions: sendParent(({ event }) => ({
                  type: "PAGE_DATA_LAYER.PUSH",
                  event: "Site Search-Recent Search List Clicked",
                  search_term: event.suggestion.label
                }))
              }
            ],
            "NAVIGABLE_SUGGESTION_BTN.CLICK": {
              guard: ({ event }) => event.identifier === "recentSearch",
              actions: [
                sendParent(({ event }) => ({
                  type: "PAGE_DATA_LAYER.PUSH",
                  event: "Site Search-Recent Search List Clicked",
                  search_term: event.suggestion.label
                })),
                assign({
                  input: ({ event }) => event.suggestion.label
                }),
                raise(({ event }) => ({
                  type: "INTERNAL.SELECT_PROFILE_SUGGESTION",
                  suggestion: event.suggestion
                }))
              ]
            }
          }
        },
        loading: {
          on: {
            "FETCH_KEYWORD_SUGGESTIONS.RESOLVED": [
              {
                guard: or(["hasCategorySuggestions", "hasProfileSuggestions"]),
                actions: ["mapCategorySuggestions", "mapProfileSuggestions", "assignAutoComplete"],
                target: [
                  "resolved.withSuggestions",
                  "#keywordDialogActor.confirmedSearchArea.hist"
                ]
              },
              {
                actions: assign({ categorySuggestions: [], profileSuggestions: [] }),
                target: [
                  "resolved.withoutSuggestions",
                  "#keywordDialogActor.confirmedSearchArea.hist"
                ]
              }
            ],
            "FETCH_KEYWORD_SUGGESTIONS.REJECTED": "error"
          }
        },
        resolved: {
          initial: "withoutSuggestions",
          states: {
            withoutSuggestions: {
              on: {
                "SELECTABLE_SUGGESTION_BTN.CLICK": {
                  guard: ({ event }) => event.identifier === "trendingSearch",
                  actions: sendParent(({ event }) => ({
                    type: "PAGE_DATA_LAYER.PUSH",
                    event: "Site Search-Trending Search List Clicked",
                    search_term: event.suggestion.label
                  }))
                }
              }
            },
            withSuggestions: {
              initial: "showAutoComplete",
              states: {
                hist: { type: "history" },
                showAutoComplete: {
                  on: {
                    "SELECTABLE_SUGGESTION_BTN.CLICK": "hideAutoComplete",
                    "NAVIGABLE_SUGGESTION_BTN.CLICK": {
                      actions: [
                        sendParent(({ event }) => ({
                          type: "PAGE_DATA_LAYER.PUSH",
                          event: "Site Search-Business Search List Clicked",
                          search_term: event.suggestion.label
                        })),
                        assign({
                          input: ({ event }) => event.suggestion.label
                        }),
                        raise(({ event }) => ({
                          type: "INTERNAL.SELECT_PROFILE_SUGGESTION",
                          suggestion: event.suggestion
                        }))
                      ],
                      target: ["hideAutoComplete", "#keywordDialogActor.confirmedSearchArea.hist"]
                    },
                    "KEY.ENTER": {
                      actions: [
                        raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                        sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
                      ],
                      target: ["hideAutoComplete", "#keywordDialogActor.confirmedSearchArea.hist"]
                    },
                    "KEY.TAB": {
                      actions: raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                      target: ["hideAutoComplete", "#keywordDialogActor.confirmedSearchArea.hist"]
                    },
                    "KEY.ARROW_RIGHT": {
                      actions: raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                      target: ["hideAutoComplete", "#keywordDialogActor.confirmedSearchArea.hist"]
                    },
                    "KEY.ARROW_DOWN": {
                      actions: {
                        type: "navigateElements",
                        params: ({ context }) => ({
                          direction: "next",
                          classname: context.elementIdentifiers.KEYWORD_SUGGESTION_BTN_CLASSNAME
                        })
                      },
                      target: ["hideAutoComplete", "#keywordDialogActor.confirmedSearchArea.hist"]
                    }
                  }
                },
                hideAutoComplete: {
                  on: {
                    "INPUT.CHANGED": {
                      actions: raise(({ event }) => ({
                        type: "INTERNAL.SEARCH",
                        value: event.value
                      })),
                      target: ["showAutoComplete", "#keywordDialogActor.confirmedSearchArea.hist"]
                    },
                    "KEY.ENTER": {
                      actions: sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
                    },
                    "KEY.TAB": {
                      guard: ({ context }) => !!context.selectedProfile,
                      actions: [
                        assign({ selectedProfile: null }),
                        {
                          type: "revertInput",
                          params: ({ context }) => ({
                            inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
                          })
                        }
                      ],
                      target: ["showAutoComplete", "#keywordDialogActor.confirmedSearchArea.hist"]
                    },
                    "KEY.ESCAPE": {
                      actions: [
                        assign({ selectedCategory: null }),
                        {
                          type: "revertInput",
                          params: ({ context }) => ({
                            inputId: context.elementIdentifiers.KEYWORD_FIELD_ID
                          })
                        },
                        {
                          type: "focusElement",
                          params: ({ context }) => ({
                            id: context.elementIdentifiers.KEYWORD_FIELD_ID
                          })
                        }
                      ],
                      target: ["showAutoComplete", "#keywordDialogActor.confirmedSearchArea.hist"]
                    },
                    "KEY.ARROW_UP": [
                      {
                        guard: {
                          type: "hasPreviousElements",
                          params: ({ context }) => ({
                            classname: context.elementIdentifiers.KEYWORD_SUGGESTION_BTN_CLASSNAME
                          })
                        },
                        actions: [
                          {
                            type: "navigateElements",
                            params: ({ context }) => ({
                              direction: "previous",
                              classname: context.elementIdentifiers.KEYWORD_SUGGESTION_BTN_CLASSNAME
                            })
                          }
                        ]
                      },
                      {
                        actions: raise({ type: "KEY.ESCAPE" })
                      }
                    ]
                  }
                }
              }
            }
          }
        },
        error: {}
      }
    }
  }
}).provide({
  actions: keywordDialogActorActions,
  guards: keywordDialogActorGuards
});
function useCreateKeywordDialogActor() {
  const keywordDialogActorActors = useCreateKeywordDialogActorActors();
  const mapProfileSuggestions = useCreateMapProfileSuggestions();
  return keywordDialogActor.provide({
    actors: keywordDialogActorActors,
    actions: {
      mapProfileSuggestions
    }
  });
}
const locationSuggestQuery = gql(`
  query locationSuggest($query: String!, $first: Int!, $enabled: Boolean) {
    searchAreaSuggest(query: $query, first: $first, enabled: $enabled) {
      edges {
        node {
          description
          id
          name
          path
          statisticalAreas {
            id
          }
        }
      }
    }
  }
`);
function useLocationSuggest() {
  const client = useApolloClient();
  async function fetchLocationSuggestions(input) {
    return client.query({
      query: locationSuggestQuery,
      variables: {
        first: 4,
        enabled: true,
        query: input.query || ""
      }
    }).then((res) => res.data);
  }
  return fetchLocationSuggestions;
}
const nearbyLocationsQuery = gql(`
  query nearbyLocations($first: Int!, $enabled: Boolean, $coordinates: String) {
    searchAreaSearch(first: $first, coordinates: $coordinates, enabled: $enabled) {
      edges {
        node {
          description
          id
          name
          path
          statisticalAreas {
            id
          }
        }
      }
    }
  }
`);
function useNearbyLocations() {
  const client = useApolloClient();
  async function fetchNearbyLocations({ coordinates }) {
    return client.query({
      query: nearbyLocationsQuery,
      variables: {
        first: 5,
        enabled: true,
        coordinates: `POINT (${String(coordinates == null ? void 0 : coordinates.longitude)} ${String(coordinates == null ? void 0 : coordinates.latitude)})`
      }
    }).then((res) => res.data);
  }
  return fetchNearbyLocations;
}
const getUserLocationActor = fromPromise(
  () => new Promise((res, rej) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(res, rej);
    } else {
      rej(new Error("Geolocation not enabled"));
    }
  })
);
function useCreateFetchNearbyLocationsActor() {
  const fetchNearbyLocations = useNearbyLocations();
  return fromPromise(
    ({ input }) => fetchNearbyLocations(input)
  );
}
function useCreateNearbyLocationsActorActors() {
  const fetchNearbyLocationsActor = useCreateFetchNearbyLocationsActor();
  return {
    getUserLocationActor,
    fetchNearbyLocationsActor
  };
}
function enqueueActions$2(params) {
  return enqueueActions$4(params);
}
const mapNearbyLocations = enqueueActions$2(({ event, enqueue }) => {
  const { edges } = event.output.searchAreaSearch || {};
  if (!(edges == null ? void 0 : edges.length)) {
    enqueue(assign({ nearbyLocations: [], currentLocation: null }));
    return;
  }
  const mapped = edges.reduce((agg, edge) => {
    const { node } = edge || {};
    if (node == null ? void 0 : node.description) {
      agg.push({
        id: node.id,
        label: node.description,
        shortLabel: node.name,
        path: node.path,
        inputValue: node.description,
        statisticalAreas: node.statisticalAreas.filter((value) => !!value)
      });
    }
    return agg;
  }, []);
  enqueue(
    assign({
      currentLocation: mapped[0],
      nearbyLocations: edges.length === 1 ? [] : mapped.slice(1)
    })
  );
});
const updateCurrentLocation = enqueueActions$2(
  ({ context, event, enqueue }) => {
    var _a;
    if (((_a = context.currentLocation) == null ? void 0 : _a.id) === event.suggestion.id) {
      return;
    }
    const updatedNearbyLocations = context.nearbyLocations.filter(
      ({ id }) => id !== event.suggestion.id
    );
    if (context.currentLocation) {
      updatedNearbyLocations.unshift(context.currentLocation);
    }
    enqueue(
      assign({
        currentLocation: event.suggestion,
        nearbyLocations: updatedNearbyLocations
      })
    );
  }
);
const nearbyLocationsActorActions = {
  mapNearbyLocations,
  focusElement,
  navigateElements,
  setInputToSuggestion,
  updateCurrentLocation
};
const nearbyLocationsActorContext = {
  userCoordinates: null,
  currentLocation: null,
  nearbyLocations: [],
  fetchNearbyLocationsActor: void 0
};
const nearbyLocationsActor = createMachine({
  id: "nearbyLocationsActor",
  types: {},
  context: nearbyLocationsActorContext,
  on: {
    "USE_MY_LOCATION_BTN.CLICK": ".loading.gettingUserLocation"
  },
  initial: "initial",
  states: {
    hist: { type: "history", history: "deep" },
    initial: {},
    loading: {
      initial: "gettingUserLocation",
      states: {
        gettingUserLocation: {
          invoke: {
            src: "getUserLocationActor",
            onDone: {
              actions: assign({
                userCoordinates: ({ event }) => ({
                  longitude: event.output.coords.longitude,
                  latitude: event.output.coords.latitude
                })
              }),
              target: "fetchingNearbyLocations"
            },
            onError: "#nearbyLocationsActor.disabled"
          }
        },
        fetchingNearbyLocations: {
          invoke: {
            src: "fetchNearbyLocationsActor",
            input: ({ context }) => ({
              coordinates: context.userCoordinates
            }),
            onDone: {
              actions: ["mapNearbyLocations", sendParent({ type: "NEARBY_LOCATIONS.RESOLVE" })],
              target: "#nearbyLocationsActor.resolved"
            },
            onError: {
              actions: assign({ nearbyLocations: [] }),
              target: "#nearbyLocationsActor.disabled"
            }
          }
        }
      }
    },
    resolved: {
      on: {
        "SELECTABLE_SUGGESTION_BTN.CLICK": { actions: "updateCurrentLocation" }
      }
    },
    disabled: {}
  }
}).provide({
  actions: nearbyLocationsActorActions
});
function useCreateNearbyLocationsActor() {
  const nearbyLocationsActorActors = useCreateNearbyLocationsActorActors();
  return nearbyLocationsActor.provide({
    actors: nearbyLocationsActorActors
  });
}
function useCreateLocationSuggestionsActor() {
  const fetchLocationSuggestions = useLocationSuggest();
  return searchSuggestionsActor.provide({
    actors: {
      fetchActor: fromPromise(
        ({ input }) => fetchLocationSuggestions(input)
      )
    }
  });
}
function useCreateLocationDialogActorActors() {
  const locationSuggestionsActor = useCreateLocationSuggestionsActor();
  const nearbyLocationsActor2 = useCreateNearbyLocationsActor();
  return {
    locationSuggestionsActor,
    nearbyLocationsActor: nearbyLocationsActor2
  };
}
function enqueueActions$1(params) {
  return enqueueActions$4(params);
}
const mapSearchAreaSuggestions = enqueueActions$1(
  ({ context, event, enqueue }) => {
    var _a, _b;
    enqueue(
      assign({
        searchAreaSuggestions: ((_b = (_a = event.data.searchAreaSuggest) == null ? void 0 : _a.edges) == null ? void 0 : _b.length) ? event.data.searchAreaSuggest.edges.reduce((agg, edge) => {
          const { node } = edge || {};
          if (node == null ? void 0 : node.description) {
            agg.push({
              id: node.id,
              label: node.description,
              shortLabel: node.name,
              labelParts: highlightMatchingCharacters(context.input, node.description),
              path: node.path.replace(/region|online|-region|-online/gi, ""),
              statisticalAreas: node.statisticalAreas.filter((value) => !!value)
            });
          }
          return agg;
        }, []) : []
      })
    );
  }
);
const selectFocusedSearchArea = enqueueActions$1(({ context, enqueue }) => {
  var _a;
  const focusedSearchAreaId = (_a = document.activeElement) == null ? void 0 : _a.id.replace("suggestBtn-", "");
  if (focusedSearchAreaId) {
    const focusedCategory = context.searchAreaSuggestions.find(
      ({ id }) => id === focusedSearchAreaId
    );
    enqueue(assign({ selectedSearchArea: focusedCategory || null }));
    return;
  }
  enqueue(assign({ selectedSearchArea: null }));
});
const assignAutoComplete = enqueueActions$1(({ context, enqueue }) => {
  var _a;
  const { input, searchAreaSuggestions } = context;
  const topSearchAreaSuggestion = searchAreaSuggestions[0];
  if (!input.length || !topSearchAreaSuggestion) {
    enqueue(
      assign({
        autoComplete: {
          input,
          autocomplete: void 0,
          suggestion: null
        }
      })
    );
    return;
  }
  const commonPrefixPattern = new RegExp(`^(${input})`, "i");
  const commonPrefixMatch = topSearchAreaSuggestion.label.match(commonPrefixPattern);
  const autocomplete2 = ((_a = commonPrefixMatch == null ? void 0 : commonPrefixMatch[1]) == null ? void 0 : _a.length) ? topSearchAreaSuggestion.label.slice(commonPrefixMatch[1].length) : void 0;
  const suggestion = autocomplete2 || input.length && input.toLowerCase() === topSearchAreaSuggestion.label.toLowerCase() ? topSearchAreaSuggestion : null;
  enqueue(
    assign({
      autoComplete: {
        input,
        autocomplete: autocomplete2,
        suggestion
      }
    })
  );
});
const addInitialSearchAreaToRecentLocationSearches = enqueueActions$1(
  ({ event, enqueue }) => {
    const updatedRecentLocations = updateRecentSearchesInLocalStorage(
      event.initialSearchArea,
      "selectable",
      "location"
    );
    enqueue(assign({ recentLocationSearches: updatedRecentLocations }));
  }
);
const clearAllRecentLocationSearches = enqueueActions$1(({ enqueue }) => {
  window.localStorage.removeItem(LOCALSTORAGE.RECENT_LOCATIONS);
  enqueue(assign({ recentLocationSearches: [] }));
});
const deleteRecentLocationSearch = enqueueActions$1(
  ({ context, event, enqueue }) => {
    const filteredRecentSearches = context.recentLocationSearches.filter(
      ({ id }) => id !== event.recentSearch.id
    );
    if (filteredRecentSearches.length) {
      window.localStorage.setItem(
        LOCALSTORAGE.RECENT_KEYWORDS,
        JSON.stringify(filteredRecentSearches)
      );
    } else {
      window.localStorage.removeItem(LOCALSTORAGE.RECENT_LOCATIONS);
    }
    enqueue(assign({ recentLocationSearches: filteredRecentSearches }));
  }
);
const selectSearchAreaFromRecentLocations = enqueueActions$1(
  ({ context, event, enqueue }) => {
    let selectedSearchArea = null;
    if (!event.filterState) {
      selectedSearchArea = event.recentLocationSearches[0] || null;
    } else {
      selectedSearchArea = event.recentLocationSearches.find(({ label: label2 }) => {
        const splitDescription = label2.match(/\b(\w+)\s+\w+\s*$/);
        return (splitDescription == null ? void 0 : splitDescription[1]) === event.filterState;
      }) || null;
    }
    enqueue(
      assign({
        selectedSearchArea,
        input: (selectedSearchArea == null ? void 0 : selectedSearchArea.label) || ""
      })
    );
    if (selectedSearchArea) {
      enqueue({
        type: "setInputToSuggestion",
        params: {
          suggestion: selectedSearchArea,
          inputId: context.elementIdentifiers.LOCATION_FIELD_ID
        }
      });
    }
  }
);
const locationDialogActorActions = {
  mapSearchAreaSuggestions,
  navigateElements,
  setInputToSuggestion,
  revertInput,
  focusElement,
  selectFocusedSearchArea,
  assignAutoComplete,
  addInitialSearchAreaToRecentLocationSearches,
  clearAllRecentLocationSearches,
  deleteRecentLocationSearch,
  selectSearchAreaFromRecentLocations
};
const locationDialogActorContext = {
  input: "",
  highlightedInput: "",
  searchAreaSuggestions: [],
  selectedSearchArea: null,
  autoComplete: { input: "", suggestion: null },
  selectedCoverageState: null,
  recentLocationSearches: [],
  locationSuggestionsActor: void 0,
  nearbyLocationsActor: void 0,
  elementIdentifiers: createElementIdentifiers("")
};
const hasMultipleSearchAreaSuggestions = ({ event }) => {
  var _a, _b;
  return !!((_b = (_a = event.data.searchAreaSuggest) == null ? void 0 : _a.edges) == null ? void 0 : _b.length) && event.data.searchAreaSuggest.edges.length > 1;
};
const hasOneSearchAreaSuggestion = ({ event }) => {
  var _a, _b;
  return !!((_b = (_a = event.data.searchAreaSuggest) == null ? void 0 : _a.edges) == null ? void 0 : _b.length) && event.data.searchAreaSuggest.edges.length === 1;
};
const locationDialogActorGuards = {
  hasPreviousElements,
  hasMultipleSearchAreaSuggestions,
  hasOneSearchAreaSuggestion
};
const locationDialogActor = createMachine({
  id: "locationDialogActor",
  types: {},
  context: ({ spawn }) => ({
    ...locationDialogActorContext,
    locationSuggestionsActor: spawn("locationSuggestionsActor"),
    nearbyLocationsActor: spawn("nearbyLocationsActor")
  }),
  entry: [
    sendTo(({ context }) => context.locationSuggestionsActor, {
      type: "FETCH.SET_CALLBACK_PREFIX",
      callbackPrefix: "FETCH_LOCATION_SUGGESTIONS"
    })
  ],
  on: {
    "KEYWORD_INPUT.FOCUS": {
      target: [".confirmedCategory.confirmedCategory", ".suggestions.hist"]
    },
    "INTERNAL.REFETCH_KEYWORD_SUGGESTIONS": {
      actions: sendTo(
        ({ system }) => system.get("keywordDialogActor"),
        ({ context }) => ({
          type: "KEYWORD_DIALOG.REFETCH",
          selectedSearchArea: context.selectedSearchArea
        })
      )
    },
    "INTERNAL.SELECT_AUTOCOMPLETE": {
      guard: ({ context }) => !!context.autoComplete.suggestion,
      actions: [
        assign({
          autoComplete: locationDialogActorContext.autoComplete,
          selectedSearchArea: ({ context }) => context.autoComplete.suggestion
        }),
        {
          type: "setInputToSuggestion",
          params: ({ context }) => ({
            inputId: context.elementIdentifiers.LOCATION_FIELD_ID,
            suggestion: context.selectedSearchArea
          })
        }
      ]
    },
    "INTERNAL.SELECT_SEARCH_AREA_SUGGESTION": {
      actions: [
        assign({
          autoComplete: locationDialogActorContext.autoComplete,
          selectedCoverageState: locationDialogActorContext.selectedCoverageState,
          selectedSearchArea: ({ event }) => event.suggestion
        }),
        raise({ type: "INTERNAL.REFETCH_KEYWORD_SUGGESTIONS" }),
        sendParent({ type: "SEARCH.ATTEMPT", trigger: "suggestionButtonClick" }),
        sendTo(({ system }) => system.get("keywordDialogActor"), {
          type: "SEARCH_AREA.CONFIRM"
        }),
        {
          type: "setInputToSuggestion",
          params: ({ event, context }) => ({
            inputId: context.elementIdentifiers.LOCATION_FIELD_ID,
            suggestion: event.suggestion
          })
        }
      ]
    },
    "INTERNAL.SELECT_COVERAGE_STATE": {
      actions: [
        assign({
          autoComplete: locationDialogActorContext.autoComplete,
          selectedSearchArea: locationDialogActorContext.selectedSearchArea,
          selectedCoverageState: ({ event }) => event.suggestion
        }),
        sendParent({ type: "SEARCH.ATTEMPT", trigger: "suggestionButtonClick" }),
        {
          type: "setInputToSuggestion",
          params: ({ event, context }) => ({
            inputId: context.elementIdentifiers.LOCATION_FIELD_ID,
            suggestion: event.suggestion
          })
        }
      ]
    },
    "RECENT_LOCATION_SEARCHES.SET": {
      actions: assign({ recentLocationSearches: ({ event }) => event.recentLocationSearches })
    },
    "INITIAL_SEARCH_AREA.SET": {
      guard: ({ event }) => !!event.initialSearchArea,
      actions: "addInitialSearchAreaToRecentLocationSearches"
    },
    "ELEMENT_IDENTIFIERS.SET": {
      actions: assign({ elementIdentifiers: ({ event }) => event.elementIdentifiers })
    },
    "FETCH_LOCATION_SUGGESTIONS.FETCHING": {
      guard: ({ context }) => !!context.input.length,
      target: [".suggestions.loading", ".confirmedCategory.hist"]
    },
    "INPUT.CHANGED": [
      {
        guard: ({ event }) => !!event.value.length,
        actions: [
          assign({ input: ({ event }) => event.value, selectedSearchArea: null }),
          "assignAutoComplete",
          sendTo(
            ({ context }) => context.locationSuggestionsActor,
            ({ event }) => ({ type: "FETCH.DEBOUNCE", variables: { query: event.value } })
          )
        ]
      },
      {
        actions: [
          assign({
            input: locationDialogActorContext.input,
            selectedSearchArea: locationDialogActorContext.selectedSearchArea,
            autoComplete: locationDialogActorContext.autoComplete
          }),
          raise({ type: "INTERNAL.REFETCH_KEYWORD_SUGGESTIONS" })
        ],
        target: [".suggestions.empty", ".confirmedCategory.hist"]
      }
    ],
    "KEY.SHIFT_TAB": { actions: raise({ type: "INTERNAL.REFETCH_KEYWORD_SUGGESTIONS" }) },
    "KEY.ESCAPE": {
      actions: [
        assign({ selectedSearchArea: null }),
        {
          type: "revertInput",
          params: ({ context }) => ({ inputId: context.elementIdentifiers.LOCATION_FIELD_ID })
        },
        {
          type: "focusElement",
          params: ({ context }) => ({ id: context.elementIdentifiers.LOCATION_FIELD_ID })
        }
      ]
    },
    "KEY.ARROW_DOWN": {
      actions: {
        type: "navigateElements",
        params: ({ context }) => ({
          direction: "next",
          classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
        })
      }
    },
    "KEY.ARROW_UP": [
      {
        guard: {
          type: "hasPreviousElements",
          params: ({ context }) => ({
            classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
          })
        },
        actions: {
          type: "navigateElements",
          params: ({ context }) => ({
            direction: "previous",
            classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
          })
        }
      },
      { actions: raise({ type: "KEY.ESCAPE" }) }
    ],
    "SELECTABLE_SUGGESTION_BTN.FOCUS": {
      actions: [
        assign({
          selectedSearchArea: ({ event }) => event.suggestion,
          selectedCoverageState: null
        }),
        {
          type: "setInputToSuggestion",
          params: ({ context }) => ({ inputId: context.elementIdentifiers.LOCATION_FIELD_ID })
        }
      ]
    },
    "NAVIGABLE_SUGGESTION_BTN.FOCUS": {
      actions: [
        assign({
          selectedCoverageState: ({ event }) => event.suggestion,
          selectedSearchArea: null
        }),
        {
          type: "setInputToSuggestion",
          params: ({ context }) => ({ inputId: context.elementIdentifiers.LOCATION_FIELD_ID })
        }
      ]
    },
    "NAVIGABLE_SUGGESTION_BTN.CLICK": {
      actions: [
        raise(({ event }) => ({
          type: "INTERNAL.SELECT_COVERAGE_STATE",
          suggestion: event.suggestion
        })),
        assign({
          input: ({ event }) => event.suggestion.label
        })
      ]
    }
  },
  type: "parallel",
  states: {
    confirmedCategory: {
      initial: "unconfirmedCategory",
      states: {
        hist: { type: "history" },
        unconfirmedCategory: {
          on: {
            "CATEGORY.CONFIRM": {
              target: ["confirmedCategory", "#locationDialogActor.suggestions.hist"]
            },
            "MOBILE_KEY.ENTER": {
              actions: [
                sendTo(({ system }) => system.get("keywordDialogActor"), {
                  type: "SEARCH_AREA.CONFIRM"
                }),
                {
                  type: "focusElement",
                  params: ({ context }) => ({
                    id: context.elementIdentifiers.KEYWORD_FIELD_ID
                  })
                }
              ],
              target: ["confirmedCategory", "#locationDialogActor.suggestions.hist"]
            },
            "SELECTABLE_SUGGESTION_BTN.CLICK": {
              actions: [
                assign({
                  input: ({ event }) => event.suggestion.label,
                  selectedSearchArea: ({ event }) => event.suggestion,
                  selectedCoverageState: null
                }),
                sendTo(({ system }) => system.get("keywordDialogActor"), {
                  type: "SEARCH_AREA.CONFIRM"
                }),
                {
                  type: "setInputToSuggestion",
                  params: ({ event, context }) => ({
                    suggestion: event.suggestion,
                    inputId: context.elementIdentifiers.LOCATION_FIELD_ID
                  })
                },
                {
                  type: "focusElement",
                  params: ({ context }) => ({
                    id: context.elementIdentifiers.KEYWORD_FIELD_ID
                  })
                }
              ]
            }
          }
        },
        confirmedCategory: {
          on: {
            "CATEGORY.UNCONFIRM": "unconfirmedCategory",
            "MOBILE_KEY.ENTER": {
              actions: [
                sendTo(({ system }) => system.get("keywordDialogActor"), {
                  type: "SEARCH_AREA.CONFIRM"
                }),
                sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
              ]
            },
            "SELECTABLE_SUGGESTION_BTN.CLICK": {
              actions: [
                assign({
                  input: ({ event }) => event.suggestion.label
                }),
                raise(({ event }) => ({
                  type: "INTERNAL.SELECT_SEARCH_AREA_SUGGESTION",
                  suggestion: event.suggestion
                }))
              ]
            }
          }
        }
      }
    },
    suggestions: {
      initial: "empty",
      states: {
        hist: { type: "history", history: "deep" },
        empty: {
          on: {
            "NEARBY_LOCATIONS.RESOLVE": {
              actions: [
                {
                  type: "setInputToSuggestion",
                  params: ({ context }) => ({
                    suggestion: context.nearbyLocationsActor.getSnapshot().context.currentLocation || void 0,
                    inputId: context.elementIdentifiers.LOCATION_FIELD_ID
                  })
                },
                assign({
                  selectedSearchArea: ({ context }) => context.nearbyLocationsActor.getSnapshot().context.currentLocation,
                  selectedCoverageState: null
                })
              ]
            },
            "USE_MY_LOCATION_BTN.FOCUS": {
              actions: [
                assign({ selectedSearchArea: null }),
                ({ context }) => ({
                  type: "revertInput",
                  params: { inputId: context.elementIdentifiers.LOCATION_FIELD_ID }
                })
              ]
            },
            "USE_MY_LOCATION_BTN.CLICK": {
              actions: [
                {
                  type: "focusElement",
                  params: ({ context }) => ({ id: context.elementIdentifiers.LOCATION_FIELD_ID })
                },
                forwardTo(({ context }) => context.nearbyLocationsActor)
              ]
            },
            "KEY.ENTER": {
              actions: sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
            },
            "CLEAR_ALL_RECENT_SEARCHES_BTN.CLICK": { actions: "clearAllRecentLocationSearches" },
            "DELETE_RECENT_SEARCH_BTN.CLICK": { actions: "deleteRecentLocationSearch" },
            "INITIAL_SEARCH_AREA.SET": {
              guard: ({ event }) => !!event.initialSearchArea,
              actions: [
                {
                  type: "setInputToSuggestion",
                  params: ({ context, event }) => ({
                    suggestion: event.initialSearchArea,
                    inputId: context.elementIdentifiers.LOCATION_FIELD_ID
                  })
                },
                "addInitialSearchAreaToRecentLocationSearches",
                assign({
                  selectedSearchArea: ({ event }) => event.initialSearchArea,
                  input: ({ event }) => event.initialSearchArea.label
                })
              ]
            },
            "RECENT_LOCATION_SEARCHES.SET": [
              {
                description: "If no initial search area has been provided then set the initial search area to the most recently searched",
                guard: ({ context, event }) => !context.selectedSearchArea && !!event.recentLocationSearches.length,
                actions: [
                  assign({
                    recentLocationSearches: ({ event }) => event.recentLocationSearches
                  }),
                  "selectSearchAreaFromRecentLocations"
                ]
              },
              {
                actions: assign({
                  recentLocationSearches: ({ event }) => event.recentLocationSearches
                })
              }
            ],
            "SELECTABLE_SUGGESTION_BTN.CLICK": {
              actions: sendParent(({ event }) => ({
                type: "PAGE_DATA_LAYER.PUSH",
                event: "Site Search-Recent Location Selected",
                search_location: event.suggestion.label
              }))
            }
          }
        },
        loading: {
          on: {
            "FETCH_LOCATION_SUGGESTIONS.RESOLVED": [
              {
                guard: "hasMultipleSearchAreaSuggestions",
                actions: ["mapSearchAreaSuggestions", "assignAutoComplete"],
                target: [
                  "resolved.withMultipleSuggestions",
                  "#locationDialogActor.confirmedCategory.hist"
                ]
              },
              {
                guard: "hasOneSearchAreaSuggestion",
                actions: ["mapSearchAreaSuggestions", "assignAutoComplete"],
                target: [
                  "resolved.withOneSuggestion",
                  "#locationDialogActor.confirmedCategory.hist"
                ]
              },
              {
                actions: assign({ searchAreaSuggestions: [] }),
                target: [
                  "resolved.withoutSuggestions",
                  "#locationDialogActor.confirmedCategory.hist"
                ]
              }
            ],
            "FETCH_LOCATION_SUGGESTIONS.REJECTED": "error"
          }
        },
        resolved: {
          initial: "withoutSuggestions",
          states: {
            withoutSuggestions: {},
            withOneSuggestion: {
              initial: "showAutoComplete",
              states: {
                showAutoComplete: {
                  on: {
                    "SELECTABLE_SUGGESTION_BTN.CLICK": "hideAutoComplete",
                    "NAVIGABLE_SUGGESTION_BTN.CLICK": {
                      actions: [
                        assign({
                          input: ({ event }) => event.suggestion.label
                        }),
                        raise(({ event }) => ({
                          type: "INTERNAL.SELECT_COVERAGE_STATE",
                          suggestion: event.suggestion
                        }))
                      ],
                      target: "hideAutoComplete"
                    },
                    "KEY.ENTER": {
                      actions: [
                        raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                        sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
                      ],
                      target: ["hideAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    },
                    "KEY.ARROW_DOWN": {
                      actions: {
                        type: "navigateElements",
                        params: ({ context }) => ({
                          direction: "next",
                          classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
                        })
                      },
                      target: ["hideAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    },
                    "KEY.ARROW_RIGHT": {
                      actions: raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                      target: ["hideAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    },
                    "KEY.TAB": {
                      actions: raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                      target: ["hideAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    }
                  }
                },
                hideAutoComplete: {
                  on: {
                    "KEY.ENTER": {
                      actions: sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
                    },
                    "KEY.ESCAPE": {
                      actions: [
                        assign({ selectedSearchArea: null }),
                        {
                          type: "revertInput",
                          params: ({ context }) => ({
                            inputId: context.elementIdentifiers.LOCATION_FIELD_ID
                          })
                        },
                        {
                          type: "focusElement",
                          params: ({ context }) => ({
                            id: context.elementIdentifiers.LOCATION_FIELD_ID
                          })
                        }
                      ],
                      target: ["showAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    },
                    "KEY.ARROW_UP": [
                      {
                        guard: {
                          type: "hasPreviousElements",
                          params: ({ context }) => ({
                            classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
                          })
                        },
                        actions: {
                          type: "navigateElements",
                          params: ({ context }) => ({
                            direction: "previous",
                            classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
                          })
                        }
                      },
                      { actions: raise({ type: "KEY.ESCAPE" }) }
                    ]
                  }
                }
              }
            },
            withMultipleSuggestions: {
              initial: "showAutoComplete",
              states: {
                showAutoComplete: {
                  on: {
                    "SELECTABLE_SUGGESTION_BTN.CLICK": "hideAutoComplete",
                    "KEY.ENTER": {
                      actions: [
                        raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                        sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
                      ],
                      target: ["hideAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    },
                    "KEY.ARROW_DOWN": {
                      actions: {
                        type: "navigateElements",
                        params: ({ context }) => ({
                          direction: "next",
                          classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
                        })
                      },
                      target: ["hideAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    },
                    "KEY.ARROW_RIGHT": {
                      actions: raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                      target: ["hideAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    },
                    "KEY.TAB": {
                      actions: raise({ type: "INTERNAL.SELECT_AUTOCOMPLETE" }),
                      target: ["hideAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    }
                  }
                },
                hideAutoComplete: {
                  on: {
                    "KEY.ENTER": {
                      actions: sendParent({ type: "SEARCH.ATTEMPT", trigger: "enterKey" })
                    },
                    "KEY.ESCAPE": {
                      actions: [
                        assign({ selectedSearchArea: null }),
                        {
                          type: "revertInput",
                          params: ({ context }) => ({
                            inputId: context.elementIdentifiers.LOCATION_FIELD_ID
                          })
                        },
                        {
                          type: "focusElement",
                          params: ({ context }) => ({
                            id: context.elementIdentifiers.LOCATION_FIELD_ID
                          })
                        }
                      ],
                      target: ["showAutoComplete", "#locationDialogActor.confirmedCategory.hist"]
                    },
                    "KEY.ARROW_UP": [
                      {
                        guard: {
                          type: "hasPreviousElements",
                          params: ({ context }) => ({
                            classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
                          })
                        },
                        actions: {
                          type: "navigateElements",
                          params: ({ context }) => ({
                            direction: "previous",
                            classname: context.elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME
                          })
                        }
                      },
                      { actions: raise({ type: "KEY.ESCAPE" }) }
                    ]
                  }
                }
              }
            }
          }
        },
        error: {}
      }
    }
  }
}).provide({
  actions: locationDialogActorActions,
  guards: locationDialogActorGuards
});
function useCreateLocationDialogActor() {
  const locationDialogActorActors = useCreateLocationDialogActorActors();
  return locationDialogActor.provide({
    actors: locationDialogActorActors
  });
}
function createNavigateActor({ onNavigate }) {
  return fromCallback(({ receive }) => {
    receive((event) => {
      if (event.type === "NAVIGATE.GO" && event.path) {
        const path = appendUTMParams(event.path);
        if (onNavigate) {
          onNavigate(path);
        } else {
          window.location.href = path;
        }
      }
    });
  });
}
function useCreateSearchMachineActors({ onNavigate }) {
  const keywordDialogActor2 = useCreateKeywordDialogActor();
  const locationDialogActor2 = useCreateLocationDialogActor();
  const navigateActor = createNavigateActor({ onNavigate });
  return {
    keywordDialogActor: keywordDialogActor2,
    locationDialogActor: locationDialogActor2,
    navigateActor
  };
}
function enqueueActions(params) {
  return enqueueActions$4(params);
}
const navigateToSelectedProfile = enqueueActions(({ context, enqueue }) => {
  const keywordActorContext = context.keywordDialogActor.getSnapshot().context;
  const { selectedProfile } = keywordActorContext;
  if (selectedProfile) {
    updateRecentSearchesInLocalStorage(selectedProfile, "navigable", "keyword");
    enqueue(sendTo("navigateActor", { type: "NAVIGATE.GO", path: selectedProfile.path }));
  }
});
const navigateToSelectedCoverageState = enqueueActions(({ context, enqueue }) => {
  const locationActorContext = context.locationDialogActor.getSnapshot().context;
  const { selectedCoverageState } = locationActorContext;
  if (selectedCoverageState) {
    enqueue(
      sendTo("navigateActor", {
        type: "NAVIGATE.GO",
        path: selectedCoverageState.path
      })
    );
  }
});
const navigateToListviewFind = enqueueActions(({ context, enqueue }) => {
  const keywordActorContext = context.keywordDialogActor.getSnapshot().context;
  const locationActorContext = context.locationDialogActor.getSnapshot().context;
  const selectedCategory = keywordActorContext.selectedCategory || keywordActorContext.autoComplete.suggestion || null;
  const selectedSearchArea = locationActorContext.selectedSearchArea || locationActorContext.autoComplete.suggestion || null;
  if ((selectedCategory == null ? void 0 : selectedCategory.path) && (selectedSearchArea == null ? void 0 : selectedSearchArea.path)) {
    updateRecentSearchesInLocalStorage(selectedCategory, "selectable", "keyword");
    updateRecentSearchesInLocalStorage(selectedSearchArea, "selectable", "location");
    enqueue(
      sendTo("navigateActor", {
        type: "NAVIGATE.GO",
        path: `/find/${selectedCategory.path}/${selectedSearchArea.path}`
      })
    );
  }
});
const navigateToListviewSearch = enqueueActions(({ context, enqueue }) => {
  const keywordActorContext = context.keywordDialogActor.getSnapshot().context;
  const locationActorContext = context.locationDialogActor.getSnapshot().context;
  const { selectedCategory } = keywordActorContext;
  const { selectedSearchArea } = locationActorContext;
  const keywordInput = document.getElementById(
    context.elementIdentifiers.KEYWORD_FIELD_ID
  );
  const locationInput = document.getElementById(
    context.elementIdentifiers.LOCATION_FIELD_ID
  );
  if ((selectedCategory || (keywordInput == null ? void 0 : keywordInput.value)) && (selectedSearchArea || (locationInput == null ? void 0 : locationInput.value))) {
    const slugifiedKeyword = (selectedCategory == null ? void 0 : selectedCategory.path) || slugify(keywordInput.value);
    const slugifiedLocation = (selectedSearchArea == null ? void 0 : selectedSearchArea.path) || slugify(locationInput.value);
    enqueue(
      sendTo("navigateActor", {
        type: "NAVIGATE.GO",
        path: `/search?what=${slugifiedKeyword}&where=${slugifiedLocation}`
      })
    );
  }
});
const fetchTrendingSearchesForInitialSearchArea = enqueueActions(({ context, enqueue }) => {
  const keywordDialogState = context.keywordDialogActor.getSnapshot();
  const locationDialogState = context.locationDialogActor.getSnapshot();
  const trendingSearchesActorState = keywordDialogState.context.fetchTrendingSearchesActor.getSnapshot();
  if (locationDialogState.context.selectedSearchArea && !keywordDialogState.context.trendingSearches.length && keywordDialogState.matches("empty") && trendingSearchesActorState.matches("idle")) {
    enqueue(
      sendTo(context.keywordDialogActor, {
        type: "KEYWORD_DIALOG.REFETCH",
        selectedSearchArea: locationDialogState.context.selectedSearchArea
      })
    );
  }
});
function pushToPageDataLayer({ context }, { search_location, search_term, ...data }) {
  const dataLayerObject = {
    user_id: context.pageDataLayerValues.userId,
    login_status: context.pageDataLayerValues.userId ? "authenticated" : "unauthenticated",
    page_type: context.pageDataLayerValues.pageType,
    cta_position_on_page: context.pageDataLayerValues.ctaPositionOnPage,
    hostname: window.location.hostname,
    page_url: window.location.href,
    search_location: search_location || context.locationDialogActor.getSnapshot().context.input,
    search_term: search_term || context.keywordDialogActor.getSnapshot().context.input,
    ...data
  };
  window.PageDataLayer = window.PageDataLayer || [];
  window.PageDataLayer.push(dataLayerObject);
  window.optimizely = window.optimizely || [];
  const { event, ...tags } = dataLayerObject;
  window.optimizely.push({
    type: "event",
    eventName: event,
    tags
  });
}
const searchMachineActions = {
  focusElement,
  navigateToSelectedProfile,
  navigateToSelectedCoverageState,
  navigateToListviewFind,
  navigateToListviewSearch,
  fetchTrendingSearchesForInitialSearchArea,
  pushToPageDataLayer
};
const searchMachineContext = {
  keywordDialogActor: void 0,
  locationDialogActor: void 0,
  elementIdentifiers: createElementIdentifiers(""),
  pageDataLayerValues: {
    userId: void 0,
    pageType: "",
    ctaPositionOnPage: ""
  }
};
const hasSelectedProfile = ({ context }) => !!context.keywordDialogActor.getSnapshot().context.selectedProfile;
const hasSelectedCoverageState = ({ context }) => !!context.locationDialogActor.getSnapshot().context.selectedCoverageState;
const hasSelectedCategoryAndSearchArea = ({ context }) => {
  const { selectedCategory } = context.keywordDialogActor.getSnapshot().context;
  const { selectedSearchArea } = context.locationDialogActor.getSnapshot().context;
  return !!((selectedCategory == null ? void 0 : selectedCategory.path) && (selectedSearchArea == null ? void 0 : selectedSearchArea.path));
};
const inputsExactMatchSuggestions = ({ context }) => {
  var _a, _b;
  const {
    autoComplete: keywordAutocomplete2,
    input: keywordInput,
    selectedCategory
  } = context.keywordDialogActor.getSnapshot().context;
  const {
    autoComplete: locationAutocomplete2,
    input: locationInput,
    selectedSearchArea
  } = context.locationDialogActor.getSnapshot().context;
  const hasValidSearchArea = !!selectedSearchArea || !!locationInput.length && ((_a = locationAutocomplete2.suggestion) == null ? void 0 : _a.label.toLowerCase()) === locationInput.toLowerCase();
  const hasValidCategory = !!selectedCategory || !!keywordInput.length && ((_b = keywordAutocomplete2.suggestion) == null ? void 0 : _b.label.toLowerCase()) === keywordInput.toLowerCase();
  return hasValidSearchArea && hasValidCategory;
};
const hasSelectedKeywordSearchCategoryAndSearchArea = ({ context }) => {
  const { selectedCategory } = context.keywordDialogActor.getSnapshot().context;
  const { selectedSearchArea } = context.locationDialogActor.getSnapshot().context;
  return !!selectedCategory && !selectedCategory.path && !!(selectedSearchArea == null ? void 0 : selectedSearchArea.path);
};
const hasSelectedCategoryAndSearchAreaAutocomplete = ({ context }) => {
  const { selectedCategory } = context.keywordDialogActor.getSnapshot().context;
  if (!selectedCategory) {
    return false;
  }
  const locationDialogActorSnapshot = context.locationDialogActor.getSnapshot();
  return !!locationDialogActorSnapshot.matches("resolved.withOneSuggestion.showAutoComplete") || !!locationDialogActorSnapshot.matches("resolved.withMultipleSuggestions.showAutoComplete");
};
const hasSelectedCategory = ({ context }) => !!context.keywordDialogActor.getSnapshot().context.selectedCategory;
const hasKeywordAndLocationInputValues = ({ context }) => {
  const keywordInput = document.getElementById(
    context.elementIdentifiers.KEYWORD_FIELD_ID
  );
  const locationInput = document.getElementById(
    context.elementIdentifiers.LOCATION_FIELD_ID
  );
  return !!(keywordInput == null ? void 0 : keywordInput.value) && !!(locationInput == null ? void 0 : locationInput.value);
};
const missingKeywordInputValue = ({ context }) => {
  const keywordInput = document.getElementById(
    context.elementIdentifiers.KEYWORD_FIELD_ID
  );
  return !(keywordInput == null ? void 0 : keywordInput.value);
};
const searchMachineGuards = {
  hasSelectedProfile,
  hasSelectedCategoryAndSearchArea,
  hasSelectedCategory,
  hasKeywordAndLocationInputValues,
  missingKeywordInputValue,
  hasSelectedCoverageState,
  hasSelectedCategoryAndSearchAreaAutocomplete,
  hasSelectedKeywordSearchCategoryAndSearchArea,
  inputsExactMatchSuggestions
};
const searchMachine = createMachine({
  id: "searchActor",
  types: {},
  context: ({ spawn }) => ({
    ...searchMachineContext,
    keywordDialogActor: spawn("keywordDialogActor", {
      id: "keywordDialogActor",
      systemId: "keywordDialogActor"
    }),
    locationDialogActor: spawn("locationDialogActor", {
      id: "locationDialogActor",
      systemId: "locationDialogActor"
    })
  }),
  invoke: { id: "navigateActor", src: "navigateActor" },
  on: {
    "SEARCH_ICON_BUTTON.CLICK": ".open.hist",
    "HOOK.OPEN_DIALOG": [
      { guard: ({ event }) => event.focus === "keyword", target: ".open.keywordFocused" },
      { guard: ({ event }) => event.focus === "location", target: ".open.locationFocused" },
      { target: ".open.hist" }
    ],
    "KEYWORD_INPUT.FOCUS": {
      actions: forwardTo(({ context }) => context.locationDialogActor),
      target: ".open.keywordFocused"
    },
    "LOCATION_INPUT.FOCUS": {
      actions: forwardTo(({ context }) => context.keywordDialogActor),
      target: ".open.locationFocused"
    },
    "PAGE_DATA_LAYER.PUSH": {
      actions: { type: "pushToPageDataLayer", params: ({ event: { type, ...data } }) => data }
    },
    "SEARCH.ATTEMPT": [
      {
        guard: or([
          "hasSelectedProfile",
          "hasSelectedCoverageState",
          "hasSelectedCategoryAndSearchArea",
          "hasSelectedKeywordSearchCategoryAndSearchArea",
          "inputsExactMatchSuggestions"
        ]),
        actions: enqueueActions$4(({ enqueue, context, event, check }) => {
          switch (event.trigger) {
            case "suggestionButtonClick":
            case "enterKey": {
              enqueue.raise({
                type: "PAGE_DATA_LAYER.PUSH",
                event: "Search Completed"
              });
              break;
            }
            case "searchButtonClick": {
              enqueue.raise({
                type: "PAGE_DATA_LAYER.PUSH",
                event: "Site Search Button Clicked"
              });
              break;
            }
          }
          enqueue.sendTo(context.locationDialogActor, { type: "CATEGORY.UNCONFIRM" });
          enqueue.sendTo(context.keywordDialogActor, { type: "SEARCH_AREA.UNCONFIRM" });
          if (check("hasSelectedProfile")) {
            enqueue("navigateToSelectedProfile");
          } else if (check("hasSelectedCoverageState")) {
            enqueue("navigateToSelectedCoverageState");
          } else if (check("hasSelectedCategoryAndSearchArea")) {
            enqueue("navigateToListviewFind");
          } else if (check("inputsExactMatchSuggestions")) {
            const keywordDialogActorContext2 = context.keywordDialogActor.getSnapshot().context;
            const locationDialogActorContext2 = context.locationDialogActor.getSnapshot().context;
            if (!keywordDialogActorContext2.selectedCategory) {
              enqueue.sendTo(context.keywordDialogActor, { type: "INTERNAL.SELECT_AUTOCOMPLETE" });
            }
            if (!locationDialogActorContext2.selectedSearchArea) {
              enqueue.sendTo(context.locationDialogActor, { type: "INTERNAL.SELECT_AUTOCOMPLETE" });
            }
            enqueue("navigateToListviewFind");
          } else if (check("hasSelectedKeywordSearchCategoryAndSearchArea")) {
            enqueue("navigateToListviewSearch");
          }
        }),
        target: ".closed.navigating"
      },
      {
        guard: "hasSelectedCategoryAndSearchAreaAutocomplete",
        actions: [
          sendTo(({ context }) => context.locationDialogActor, {
            type: "KEY.ENTER"
          })
        ]
      },
      {
        guard: "hasKeywordAndLocationInputValues",
        actions: enqueueActions$4(({ enqueue, context, event }) => {
          switch (event.trigger) {
            case "suggestionButtonClick":
            case "enterKey": {
              enqueue.raise({
                type: "PAGE_DATA_LAYER.PUSH",
                event: "Search Completed"
              });
              break;
            }
            case "searchButtonClick": {
              enqueue.raise({
                type: "PAGE_DATA_LAYER.PUSH",
                event: "Site Search Button Clicked"
              });
              break;
            }
          }
          enqueue("navigateToListviewSearch");
          enqueue.sendTo(context.locationDialogActor, { type: "CATEGORY.UNCONFIRM" });
          enqueue.sendTo(context.keywordDialogActor, { type: "SEARCH_AREA.UNCONFIRM" });
        }),
        target: ".closed.navigating"
      },
      {
        guard: "missingKeywordInputValue",
        actions: {
          type: "focusElement",
          params: ({ context }) => ({ id: context.elementIdentifiers.KEYWORD_FIELD_ID })
        },
        target: ".open.keywordFocused"
      },
      {
        actions: {
          type: "focusElement",
          params: ({ context }) => ({ id: context.elementIdentifiers.LOCATION_FIELD_ID })
        },
        target: ".open.locationFocused"
      }
    ],
    "SEARCH_BTN.CLICK": {
      actions: raise({ type: "SEARCH.ATTEMPT", trigger: "searchButtonClick" })
    },
    "RECENT_KEYWORD_SEARCHES.SET": {
      actions: forwardTo(({ context }) => context.keywordDialogActor)
    },
    "RECENT_LOCATION_SEARCHES.SET": {
      actions: forwardTo(({ context }) => context.locationDialogActor)
    },
    "INITIAL_CATEGORY.SET": {
      actions: forwardTo(({ context }) => context.keywordDialogActor)
    },
    "INITIAL_PROFILE.SET": {
      actions: forwardTo(({ context }) => context.keywordDialogActor)
    },
    "INITIAL_SEARCH_AREA.SET": {
      actions: forwardTo(({ context }) => context.locationDialogActor)
    },
    "ELEMENT_IDENTIFIERS.SET": {
      actions: [
        assign({ elementIdentifiers: ({ event }) => event.elementIdentifiers }),
        forwardTo(({ context }) => context.keywordDialogActor),
        forwardTo(({ context }) => context.locationDialogActor)
      ]
    },
    "PAGE_DATA_LAYER.SET_CONTEXT": {
      actions: assign({
        pageDataLayerValues: ({ context, event }) => ({
          userId: event.userId || context.pageDataLayerValues.userId,
          pageType: event.pageType || context.pageDataLayerValues.pageType,
          ctaPositionOnPage: event.ctaPositionOnPage || context.pageDataLayerValues.ctaPositionOnPage
        })
      })
    }
  },
  initial: "closed",
  states: {
    closed: {
      on: {
        "KEYWORD_DIALOG_BTN.CLICK": "open.keywordFocused",
        "LOCATION_DIALOG_BTN.CLICK": "open.locationFocused"
      },
      exit: raise({
        type: "PAGE_DATA_LAYER.PUSH",
        event: "Site Search Started"
      }),
      initial: "idle",
      states: {
        idle: {},
        navigating: {}
      }
    },
    open: {
      id: "open",
      on: {
        "DIALOG.CLICK_OUTSIDE": "closed.idle",
        "CLOSE_DIALOG_BTN.CLICK": "closed.idle"
      },
      initial: "keywordFocused",
      states: {
        hist: { type: "history", history: "deep" },
        keywordFocused: {
          entry: "fetchTrendingSearchesForInitialSearchArea",
          on: {
            "INPUT.CHANGED": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "KEY.TAB": {
              actions: [
                {
                  type: "focusElement",
                  params: ({ context }) => ({ id: context.elementIdentifiers.LOCATION_FIELD_ID })
                },
                forwardTo(({ context }) => context.keywordDialogActor)
              ],
              target: "locationFocused"
            },
            "KEY.ARROW_RIGHT": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "KEY.ARROW_DOWN": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "KEY.ARROW_UP": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "KEY.ESCAPE": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "KEY.ENTER": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "MOBILE_KEY.ENTER": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "SELECTABLE_SUGGESTION_BTN.FOCUS": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "SELECTABLE_SUGGESTION_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "NAVIGABLE_SUGGESTION_BTN.FOCUS": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "NAVIGABLE_SUGGESTION_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "CLEAR_ALL_RECENT_SEARCHES_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            },
            "DELETE_RECENT_SEARCH_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.keywordDialogActor)
            }
          }
        },
        locationFocused: {
          on: {
            "INPUT.CHANGED": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "KEY.TAB": {
              actions: [
                {
                  type: "focusElement",
                  params: ({ context }) => ({ id: context.elementIdentifiers.SEARCH_BUTTON_ID })
                },
                forwardTo(({ context }) => context.locationDialogActor)
              ]
            },
            "KEY.SHIFT_TAB": {
              actions: [
                {
                  type: "focusElement",
                  params: ({ context }) => ({ id: context.elementIdentifiers.KEYWORD_FIELD_ID })
                },
                forwardTo(({ context }) => context.locationDialogActor)
              ],
              target: "keywordFocused"
            },
            "KEY.ARROW_RIGHT": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "KEY.ARROW_DOWN": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "KEY.ARROW_UP": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "KEY.ESCAPE": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "KEY.ENTER": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "MOBILE_KEY.ENTER": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "SELECTABLE_SUGGESTION_BTN.FOCUS": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "SELECTABLE_SUGGESTION_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "NAVIGABLE_SUGGESTION_BTN.FOCUS": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "NAVIGABLE_SUGGESTION_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "CLEAR_ALL_RECENT_SEARCHES_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "DELETE_RECENT_SEARCH_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "USE_MY_LOCATION_BTN.FOCUS": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            },
            "USE_MY_LOCATION_BTN.CLICK": {
              actions: forwardTo(({ context }) => context.locationDialogActor)
            }
          }
        }
      }
    }
  }
}).provide({
  actions: searchMachineActions,
  guards: searchMachineGuards
});
function useCreateSearchMachine({ onNavigate }) {
  const searchMachineActors = useCreateSearchMachineActors({ onNavigate });
  return useMachine(
    searchMachine.provide({
      actors: searchMachineActors
    })
  );
}
function createElementProps(send, elementPrefix) {
  const elementIdentifiers = createElementIdentifiers(elementPrefix);
  const outsideClickHandlerProps = {
    handleOutsideClick() {
      send({ type: "DIALOG.CLICK_OUTSIDE" });
    }
  };
  const searchIconButtonProps = {
    "aria-label": "Toggle open/close of search bar",
    onClick(event) {
      event.stopPropagation();
      send({ type: "SEARCH_ICON_BUTTON.CLICK" });
    }
  };
  function createInputProps(mobile = false) {
    return {
      type: "search",
      autoComplete: "off",
      autoCorrect: "off",
      spellCheck: "false",
      "aria-haspopup": "dialog",
      onChange(event) {
        send({ type: "INPUT.CHANGED", value: event.target.value });
      },
      onKeyDown(event) {
        const keyMap = {
          Escape() {
            send({ type: "KEY.ESCAPE" });
          },
          ArrowDown() {
            event.preventDefault();
            send({ type: "KEY.ARROW_DOWN" });
          },
          ArrowUp() {
            event.preventDefault();
            send({ type: "KEY.ARROW_UP" });
          },
          ArrowRight() {
            send({ type: "KEY.ARROW_RIGHT" });
          },
          Enter() {
            event.preventDefault();
            send({ type: mobile ? "MOBILE_KEY.ENTER" : "KEY.ENTER" });
          },
          Tab() {
            event.preventDefault();
            if (event.shiftKey) {
              send({ type: "KEY.SHIFT_TAB" });
            } else {
              send({ type: "KEY.TAB" });
            }
          }
        };
        const exec = event.key in keyMap ? keyMap[event.key] : null;
        if (exec) {
          exec(event);
        }
      }
    };
  }
  function createKeywordInputProps(mobile = false) {
    return {
      ...createInputProps(mobile),
      id: elementIdentifiers.KEYWORD_FIELD_ID,
      onFocus(event) {
        event.stopPropagation();
        send({ type: "KEYWORD_INPUT.FOCUS" });
      }
    };
  }
  function createLocationInputProps(mobile = false) {
    return {
      ...createInputProps(mobile),
      id: elementIdentifiers.LOCATION_FIELD_ID,
      onFocus(event) {
        event.stopPropagation();
        send({ type: "LOCATION_INPUT.FOCUS" });
      }
    };
  }
  const searchButtonProps = {
    id: elementIdentifiers.SEARCH_BUTTON_ID,
    onClick(event) {
      event.stopPropagation();
      send({ type: "SEARCH_BTN.CLICK" });
    }
  };
  function createSuggestionButtonProps(node, category, type, identifier) {
    return {
      id: `${String(elementIdentifiers.SUGGESTION_BTN_ID)}-${node.id}`,
      className: clsx(
        category === "keyword" && elementIdentifiers.KEYWORD_SUGGESTION_BTN_CLASSNAME,
        category === "location" && elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME,
        type === "navigable" && elementIdentifiers.NAVIGABLE_SUGGESTION_BTN_CLASSNAME,
        type === "selectable" && elementIdentifiers.SELECTABLE_SUGGESTION_BTN_CLASSNAME
      ),
      onFocus() {
        if (type === "navigable") {
          send({ type: "NAVIGABLE_SUGGESTION_BTN.FOCUS", suggestion: node, identifier });
        } else {
          send({ type: "SELECTABLE_SUGGESTION_BTN.FOCUS", suggestion: node, identifier });
        }
      },
      onClick(event) {
        event.stopPropagation();
        if (type === "navigable") {
          send({ type: "NAVIGABLE_SUGGESTION_BTN.CLICK", suggestion: node, identifier });
        } else {
          send({ type: "SELECTABLE_SUGGESTION_BTN.CLICK", suggestion: node, identifier });
        }
      },
      onKeyDown(event) {
        const keyMap = {
          ArrowDown() {
            send({ type: "KEY.ARROW_DOWN" });
          },
          ArrowUp() {
            send({ type: "KEY.ARROW_UP" });
          },
          Enter() {
            if (type === "navigable") {
              send({ type: "NAVIGABLE_SUGGESTION_BTN.CLICK", suggestion: node, identifier });
            } else {
              send({ type: "SELECTABLE_SUGGESTION_BTN.CLICK", suggestion: node, identifier });
            }
          },
          Escape() {
            send({ type: "KEY.ESCAPE" });
          },
          Tab() {
            if (event.shiftKey) {
              send({ type: "KEY.SHIFT_TAB" });
            } else {
              send({ type: "KEY.TAB" });
            }
          }
        };
        const exec = event.key in keyMap ? keyMap[event.key] : null;
        if (exec) {
          event.preventDefault();
          exec(event);
        }
      }
    };
  }
  function createRecentSearchesButtonProps(node) {
    return {
      id: `${String(elementIdentifiers.SUGGESTION_BTN_ID)}-${node.id}`,
      onClick(event) {
        event.stopPropagation();
        if (node.type === "navigable") {
          send({
            type: "NAVIGABLE_SUGGESTION_BTN.CLICK",
            suggestion: node,
            identifier: "recentSearch"
          });
        } else {
          send({
            type: "SELECTABLE_SUGGESTION_BTN.CLICK",
            suggestion: node,
            identifier: "recentSearch"
          });
        }
      }
    };
  }
  function createDeleteRecentSearchButtonProps(node) {
    return {
      "aria-label": "Delete search from history",
      id: `${String(elementIdentifiers.SUGGESTION_BTN_ID)}-${node.id}`,
      onFocus(event) {
        event.stopPropagation();
      },
      onClick(event) {
        event.stopPropagation();
        send({ type: "DELETE_RECENT_SEARCH_BTN.CLICK", recentSearch: node });
      }
    };
  }
  const clearAllRecentSearchesButtonProps = {
    "aria-label": "Remove all recent searches from history",
    onClick(event) {
      event.stopPropagation();
      send({ type: "CLEAR_ALL_RECENT_SEARCHES_BTN.CLICK" });
    }
  };
  const useMyLocationButtonProps = {
    className: elementIdentifiers.LOCATION_SUGGESTION_BTN_CLASSNAME,
    onFocus() {
      send({ type: "USE_MY_LOCATION_BTN.FOCUS" });
    },
    onClick(event) {
      event.stopPropagation();
      send({ type: "USE_MY_LOCATION_BTN.CLICK" });
    },
    onKeyDown(event) {
      const keyMap = {
        ArrowDown() {
          send({ type: "KEY.ARROW_DOWN" });
        },
        ArrowUp() {
          send({ type: "KEY.ARROW_UP" });
        },
        Escape() {
          send({ type: "KEY.ESCAPE" });
        },
        Tab() {
          if (event.shiftKey) {
            send({ type: "KEY.SHIFT_TAB" });
          } else {
            send({ type: "KEY.TAB" });
          }
        }
      };
      const exec = event.key in keyMap ? keyMap[event.key] : null;
      if (exec) {
        event.preventDefault();
        exec(event);
      }
    }
  };
  const keywordInputButtonProps = {
    "aria-label": "Open search dialog and focus on keyword input",
    onClick(event) {
      event.stopPropagation();
      send({ type: "KEYWORD_DIALOG_BTN.CLICK" });
    }
  };
  const locationInputButtonProps = {
    "aria-label": "Open search dialog and focus on location input",
    onClick(event) {
      event.stopPropagation();
      send({ type: "LOCATION_DIALOG_BTN.CLICK" });
    }
  };
  const closeMobileDialogProps = {
    "aria-label": "Close search dialog",
    onClick(event) {
      event.stopPropagation();
      send({ type: "CLOSE_DIALOG_BTN.CLICK" });
    }
  };
  return {
    outsideClickHandlerProps,
    searchIconButtonProps,
    createKeywordInputProps,
    createLocationInputProps,
    searchButtonProps,
    useMyLocationButtonProps,
    createSuggestionButtonProps,
    createRecentSearchesButtonProps,
    createDeleteRecentSearchButtonProps,
    clearAllRecentSearchesButtonProps,
    keywordInputButtonProps,
    locationInputButtonProps,
    closeMobileDialogProps
  };
}
function useConnect({
  elementPrefix,
  initialCategory,
  initialProfile,
  initialSearchArea,
  pageDataLayerValues,
  ...createSearchMachineParams
}) {
  const isSsr = useIsSsr();
  const [state, send] = useCreateSearchMachine(createSearchMachineParams);
  const elementProps = useMemo(
    () => createElementProps(send, elementPrefix),
    [send, elementPrefix]
  );
  const { keywordDialogActor: keywordDialogActor2, locationDialogActor: locationDialogActor2 } = state.context;
  const keywordDialogState = useSelector(keywordDialogActor2, (snapshot) => snapshot);
  const keywordDialogContext = useSelector(keywordDialogActor2, (snapshot) => snapshot.context);
  const locationDialogState = useSelector(locationDialogActor2, (snapshot) => snapshot);
  const locationDialogContext = useSelector(locationDialogActor2, (snapshot) => snapshot.context);
  const nearbyLocationsState = useSelector(
    locationDialogContext.nearbyLocationsActor,
    (snapshot) => snapshot
  );
  const nearbyLocationsContext = useSelector(
    locationDialogContext.nearbyLocationsActor,
    (snapshot) => snapshot.context
  );
  function openDialog(focus) {
    send({ type: "HOOK.OPEN_DIALOG", focus });
  }
  useEffect(() => {
    if (elementPrefix) {
      send({
        type: "ELEMENT_IDENTIFIERS.SET",
        elementIdentifiers: createElementIdentifiers(elementPrefix)
      });
    }
  }, [elementPrefix]);
  useEffect(() => {
    send({
      type: "PAGE_DATA_LAYER.SET_CONTEXT",
      ...pageDataLayerValues
    });
  }, [pageDataLayerValues]);
  useEffect(() => {
    if (initialCategory) {
      send({ type: "INITIAL_CATEGORY.SET", initialCategory });
    }
  }, [initialCategory]);
  useEffect(() => {
    if (initialProfile) {
      send({ type: "INITIAL_PROFILE.SET", initialProfile });
    }
  }, [initialProfile]);
  useEffect(() => {
    if (initialSearchArea) {
      send({ type: "INITIAL_SEARCH_AREA.SET", initialSearchArea });
    }
  }, [initialSearchArea]);
  useEffect(() => {
    if (!isSsr) {
      const recentKeywordsJSON = window.localStorage.getItem(LOCALSTORAGE.RECENT_KEYWORDS);
      const recentLocationsJSON = window.localStorage.getItem(LOCALSTORAGE.RECENT_LOCATIONS);
      if (recentKeywordsJSON) {
        const parsedRecentSearches = JSON.parse(recentKeywordsJSON);
        const recentKeywordSearchesArray = Array.isArray(parsedRecentSearches) ? parsedRecentSearches : [];
        send({
          type: "RECENT_KEYWORD_SEARCHES.SET",
          recentKeywordSearches: recentKeywordSearchesArray.filter(
            ({ id, label: label2 }) => !!label2 && !!id
          )
        });
      }
      if (recentLocationsJSON) {
        const pathname = new URL(window.location.href).pathname.replace(/^\/|\/$/g, "").toLowerCase();
        const pathToStateCode = pathname in STATE_CODE_PATHS ? STATE_CODE_PATHS[pathname] : void 0;
        const parsedRecentSearches = JSON.parse(recentLocationsJSON);
        const recentLocationSearchesArray = Array.isArray(parsedRecentSearches) ? parsedRecentSearches : [];
        send({
          type: "RECENT_LOCATION_SEARCHES.SET",
          recentLocationSearches: recentLocationSearchesArray.filter(
            ({ id, label: label2 }) => !!label2 && !!id
          ),
          filterState: pathToStateCode
        });
      }
    }
  }, [isSsr]);
  const keywordSuggestions = {
    categorySuggestions: keywordDialogContext.categorySuggestions,
    profileSuggestions: keywordDialogContext.profileSuggestions,
    keywordAutocomplete: keywordDialogContext.autoComplete,
    trendingSearches: keywordDialogContext.trendingSearches,
    recentKeywordSearches: keywordDialogContext.recentKeywordSearches.slice(0, 4)
  };
  const locationSuggestions = {
    searchAreaSuggestions: locationDialogContext.searchAreaSuggestions,
    locationAutocomplete: locationDialogContext.autoComplete,
    nearbyLocations: nearbyLocationsContext.nearbyLocations,
    currentLocation: nearbyLocationsContext.currentLocation,
    recentLocationSearches: locationDialogContext.recentLocationSearches.slice(0, 4)
  };
  const ui = {
    // Dialog
    showDialog: state.matches("open"),
    // Keyword
    showKeywordSuggestions: state.matches("open.keywordFocused"),
    showKeywordAutocomplete: state.matches("open.keywordFocused") && !!keywordDialogState.matches("suggestions.resolved.withSuggestions.showAutoComplete"),
    showEmptyKeywordSuggestions: !!keywordDialogState.matches("suggestions.empty"),
    showRecentKeywords: !!keywordDialogState.matches("suggestions.empty") && !!keywordDialogContext.recentKeywordSearches.length,
    showLoadingKeywordSuggestions: !!keywordDialogState.matches("suggestions.loading"),
    showResolvedKeywordSuggestions: !!keywordDialogState.matches("suggestions.resolved"),
    showWithoutKeywordSuggestions: !!keywordDialogState.matches(
      "suggestions.resolved.withoutSuggestions"
    ),
    showWithKeywordSuggestions: !!keywordDialogState.matches(
      "suggestions.resolved.withSuggestions"
    ),
    showCategorySuggestions: !!keywordDialogState.matches("suggestions.resolved.withSuggestions") && !!keywordDialogContext.categorySuggestions.length,
    showProfileSuggestions: !!keywordDialogState.matches("suggestions.resolved.withSuggestions") && !!keywordDialogContext.profileSuggestions.length,
    showKeywordError: !!keywordDialogState.matches("suggestions.error"),
    // Location
    showSearchButtonLoading: state.matches("closed.navigating"),
    showLocationSuggestions: state.matches("open.locationFocused"),
    showLocationAutocomplete: state.matches("open.locationFocused") && !!(locationDialogState.matches(
      "suggestions.resolved.withMultipleSuggestions.showAutoComplete"
    ) || locationDialogState.matches("suggestions.resolved.withOneSuggestion.showAutoComplete")),
    showEmptyLocationSuggestions: !!locationDialogState.matches("suggestions.empty"),
    showRecentLocations: !!locationDialogState.matches("suggestions.empty") && !nearbyLocationsState.matches("suggestions.loading") && !!locationDialogContext.recentLocationSearches.length,
    showLoadingLocationSuggestions: !!locationDialogState.matches("suggestions.loading"),
    showResolvedLocationSuggestions: !!locationDialogState.matches("suggestions.resolved"),
    showWithoutLocationSuggestions: !!locationDialogState.matches(
      "suggestions.resolved.withoutSuggestions"
    ),
    showWithOneLocationSuggestion: !!locationDialogState.matches(
      "suggestions.resolved.withOneSuggestion"
    ),
    showWithMultipleLocationSuggestions: !!locationDialogState.matches(
      "suggestions.resolved.withMultipleSuggestions"
    ),
    showLocationError: !!locationDialogState.matches("suggestions.error"),
    // Nearby Locations
    showInitialNearbyLocations: !!nearbyLocationsState.matches("initial"),
    showLoadingNearbyLocations: !!nearbyLocationsState.matches("loading"),
    showResolvedNearbyLocations: !!nearbyLocationsState.matches("resolved"),
    showCurrentLocation: !!nearbyLocationsContext.currentLocation,
    showNearbyLocationSuggestions: !!nearbyLocationsContext.nearbyLocations.length,
    showDisabledNearbyLocations: !!nearbyLocationsState.matches("disabled")
  };
  const searchContextValues = {
    locationDialogContext,
    showDialog: ui.showDialog,
    openDialog
  };
  return {
    keywordSuggestions,
    locationSuggestions,
    ui,
    elementProps,
    searchContextValues
  };
}
const SearchConnectContext = createContext({});
const SearchContext = createContext({});
function SearchConnectProvider({ children, id, ...useConnectParams }) {
  const { searchContextValues, ...connect } = useConnect({
    elementPrefix: id,
    ...useConnectParams
  });
  return /* @__PURE__ */ jsx(SearchConnectContext.Provider, { value: connect, children: /* @__PURE__ */ jsx(SearchContext.Provider, { value: searchContextValues, children }) });
}
function useConnectContext() {
  const context = useContext(
    SearchConnectContext
  );
  if (context === void 0) {
    throw new Error("useConnectContext must be used within a SearchConnectProvider");
  }
  return context;
}
function useSearchContext() {
  const context = useContext(SearchContext);
  if (context === void 0) {
    throw new Error("useSearchContext must be used within a SearchConnectProvider");
  }
  return context;
}
const icon$3 = "_icon_1qsoj_1";
const primary = "_primary_1qsoj_9";
const black = "_black_1qsoj_13";
const large = "_large_1qsoj_16";
const styles$c = {
  icon: icon$3,
  primary,
  black,
  large
};
const Icon = forwardRef(
  ({ name, className, primary: primary2 = false, black: black2 = false, large: large2 = false, ...props }, ref) => /* @__PURE__ */ jsx(
    "i",
    {
      className: clsx(
        `lsDesignIcon-${name}`,
        styles$c["icon"],
        !!primary2 && styles$c["primary"],
        !!black2 && styles$c["black"],
        !!large2 && styles$c["large"],
        className
      ),
      ref,
      ...props
    }
  )
);
const dialog$5 = "_dialog_e5nff_1";
const container$4 = "_container_e5nff_8";
const styles$b = {
  dialog: dialog$5,
  container: container$4
};
const Dialog = forwardRef(
  ({ className, children, ...props }, ref) => /* @__PURE__ */ jsx("dialog", { className: clsx(styles$b["dialog"], className), ...props, ref, children: /* @__PURE__ */ jsx("div", { className: styles$b["container"], children }) })
);
const placeholderLogo = "data:image/svg+xml,%3csvg%20width='34'%20height='34'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3crect%20width='34'%20height='34'%20rx='5'%20fill='%23fff'/%3e%3cpath%20d='M3%2018.153V14h.758v4.153H3ZM13.371%2018.153V14h.758v4.153h-.758ZM10.429%2017.37a.785.785%200%200%201%20.252-.59.856.856%200%200%201%20.273-.17c.106-.043.217-.074.33-.091l.695-.1a.273.273%200%200%200%20.167-.071.198.198%200%200%200%20.045-.126.333.333%200%200%200-.116-.253.518.518%200%200%200-.357-.126.504.504%200%200%200-.542.457l-.675-.146a1%201%200%200%201%20.327-.616c.111-.097.24-.172.379-.221.162-.058.333-.085.505-.081a1.63%201.63%200%200%201%20.567.09c.141.052.27.133.378.238a.876.876%200%200%201%20.211.342c.043.132.065.27.065.408v1.389c-.002.081.002.163.01.244.01.085.015.154.022.203h-.7a1.883%201.883%200%200%201-.028-.338.997.997%200%200%201-.312.29.972.972%200%200%201-.52.126.989.989%200%200%201-.703-.264.793.793%200%200%201-.273-.595Zm1.129.286c.078%200%20.156-.01.232-.031a.499.499%200%200%200%20.337-.308.824.824%200%200%200%20.053-.312v-.127l-.631.095a.507.507%200%200%200-.253.105.304.304%200%200%200-.1.252.316.316%200%200%200%20.215.306.355.355%200%200%200%20.147.02ZM7.021%2016.125v-.012a1.389%201.389%200%200%200-.778-.787%201.537%201.537%200%200%200-.587-.114%201.54%201.54%200%200%200-.588.11%201.39%201.39%200%200%200-.783.78c-.077.19-.116.395-.114.6a1.459%201.459%200%200%200%20.413%201.078c.133.137.292.244.469.315.186.077.386.116.588.115.201.002.4-.036.587-.11a1.404%201.404%200%200%200%20.784-.783v-.011a2.046%202.046%200%200%201%20.01-1.18Zm-.72.936a.773.773%200%200%201-.157.253.64.64%200%200%201-.231.151.728.728%200%200%201-.538-.003.648.648%200%200%201-.229-.156.77.77%200%200%201-.156-.253.921.921%200%200%201-.059-.35.915.915%200%200%201%20.061-.349.778.778%200%200%201%20.16-.253.642.642%200%200%201%20.229-.151.735.735%200%200%201%20.54.002.65.65%200%200%201%20.228.153.774.774%200%200%201%20.157.253c.042.111.062.23.059.35a.913.913%200%200%201-.064.353ZM8.844%2015.93a.724.724%200%200%200-.279.05.655.655%200%200%200-.382.404%201.01%201.01%200%200%200-.007.669.75.75%200%200%200%20.157.252c.065.067.143.12.229.156a.746.746%200%200%200%20.277.053.671.671%200%200%200%20.446-.153.598.598%200%200%200%20.126-.155.775.775%200%200%200%20.072-.166l.67.226a1.223%201.223%200%200%201-.427.65%201.362%201.362%200%200%201-.895.297c-.2%200-.398-.039-.583-.115a1.438%201.438%200%200%201-.785-.793%201.548%201.548%200%200%201-.112-.6c-.001-.206.038-.41.115-.602a1.399%201.399%200%200%201%20.777-.779c.183-.075.379-.112.576-.11.175-.001.349.027.514.085.141.052.273.125.391.217.108.085.199.189.27.306.065.109.114.227.147.35l-.684.227a.766.766%200%200%200-.196-.328.571.571%200%200%200-.417-.14ZM14.143%2020.452H3v-.757h11.143v.757ZM15.152%2018.13a1.211%201.211%200%200%201-.341-.213.976.976%200%200%201-.32-.565v-.017l.367-.127v.023a.719.719%200%200%200%20.228.424.77.77%200%200%200%20.54.18.716.716%200%200%200%20.44-.127.405.405%200%200%200%20.177-.343.36.36%200%200%200-.127-.29.791.791%200%200%200-.345-.16l-.455-.114a1.054%201.054%200%200%201-.518-.262.657.657%200%200%201-.195-.505.69.69%200%200%201%20.082-.327.866.866%200%200%201%20.214-.264%201.077%201.077%200%200%201%20.69-.242c.151-.003.3.02.443.068a.943.943%200%200%201%20.308.174c.08.067.146.149.196.24.043.08.077.165.1.253v.016l-.358.126v-.022a.67.67%200%200%200-.193-.355.623.623%200%200%200-.202-.117.851.851%200%200%200-.51-.012.594.594%200%200%200-.19.095.488.488%200%200%200-.126.148.367.367%200%200%200-.062.203c0%20.22.14.36.432.427l.455.1c.21.04.404.138.56.283a.69.69%200%200%201%20.195.506.757.757%200%200%201-.069.311.837.837%200%200%201-.198.27c-.093.09-.2.163-.318.216-.291.103-.609.102-.9%200ZM17.828%2018.11a1.405%201.405%200%200%201-.75-.789%201.656%201.656%200%200%201-.11-.631%201.643%201.643%200%200%201%20.111-.604c.069-.172.168-.33.294-.465a1.285%201.285%200%200%201%20.94-.407c.197-.003.393.035.576.11.16.068.304.17.421.298.119.131.21.285.267.452.063.184.093.377.09.572v.064a.428.428%200%200%201-.007.08h-2.287v.011c0%20.141.027.281.078.413a.959.959%200%200%200%20.535.543c.123.05.255.076.387.076a.912.912%200%200%200%20.601-.186.995.995%200%200%200%20.316-.457l.356.126a1.333%201.333%200%200%201-.45.621%201.473%201.473%200%200%201-.368.209c-.328.096-.68.083-1-.037Zm.126-2.474a.927.927%200%200%200-.479.481c-.043.1-.07.205-.08.313v.023l1.864.007v-.021a1.075%201.075%200%200%200-.067-.315.866.866%200%200%200-.18-.287.793.793%200%200%200-.29-.192%201.052%201.052%200%200%200-.774-.01h.006ZM29.592%2018.155l.008-1.782a.968.968%200%200%200-.043-.3.64.64%200%200%200-.368-.409.836.836%200%200%200-.338-.06.72.72%200%200%200-.32.07.751.751%200%200%200-.246.185.956.956%200%200%200-.217.596v1.695h-.4l.018-4.142h.4l-.008%201.735.038-.066a.687.687%200%200%201%20.276-.265%201.08%201.08%200%200%201%20.574-.153c.156-.003.312.026.457.085a.894.894%200%200%201%20.515.565c.043.133.064.272.062.412l-.008%201.842-.4-.008ZM25.348%2018.115a1.408%201.408%200%200%201-.744-.792%201.629%201.629%200%200%201-.103-.596c0-.204.036-.408.109-.6a1.358%201.358%200%200%201%20.754-.779c.33-.13.695-.142%201.032-.033a1.195%201.195%200%200%201%20.77.822l-.357.137a1.135%201.135%200%200%200-.102-.242.833.833%200%200%200-.424-.375.98.98%200%200%200-.351-.062%201.01%201.01%200%200%200-.397.076.936.936%200%200%200-.323.222%201.105%201.105%200%200%200-.222.357c-.11.306-.113.64-.008.948a.977.977%200%200%200%20.94.657.942.942%200%200%200%20.354-.056.937.937%200%200%200%20.557-.597l.356.143a1.42%201.42%200%200%201-.159.323%201.156%201.156%200%200%201-.266.283%201.391%201.391%200%200%201-.371.206%201.501%201.501%200%200%201-1.045-.042ZM20.58%2018.144a.966.966%200%200%201-.295-.196.796.796%200%200%201-.008-1.148c.18-.148.4-.24.632-.266l.757-.109a.378.378%200%200%200%20.23-.09.237.237%200%200%200%20.054-.157.585.585%200%200%200-.175-.443.733.733%200%200%200-.522-.172.837.837%200%200%200-.54.165.676.676%200%200%200-.244.463v.021l-.379-.08v-.017a.965.965%200%200%201%20.38-.65c.105-.08.225-.14.352-.18.137-.044.28-.066.425-.064.168-.003.336.024.495.08a.865.865%200%200%201%20.545.53c.044.121.066.25.066.378v1.38c.006.184.026.368.06.549h-.39a3.803%203.803%200%200%201-.055-.432v-.034l-.034.059c-.024.03-.047.063-.07.088a1.464%201.464%200%200%201-.51.33%201.38%201.38%200%200%201-.774-.005Zm1.344-1.442c-.05.02-.1.032-.153.04l-.797.113a.7.7%200%200%200-.387.163.443.443%200%200%200-.143.348.46.46%200%200%200%20.149.335.593.593%200%200%200%20.44.151c.126.001.251-.019.37-.06.109-.039.208-.099.294-.176a.758.758%200%200%200%20.19-.278.978.978%200%200%200%20.07-.37v-.281l-.033.015ZM22.957%2018.15l.013-2.918h.4v.465l.038-.05a.95.95%200%200%201%20.836-.425c.08-.003.16.007.237.032l.014.006v.379s-.57-.168-.884.141a.916.916%200%200%200-.241.687l-.006%201.682-.407.001Z'%20fill='%239B9B9B'/%3e%3c/svg%3e";
const deleteIcon = "data:image/svg+xml,%3csvg%20width='20'%20height='21'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3ccircle%20cx='10'%20cy='10.5'%20r='10'%20fill='%23ECECEC'/%3e%3cpath%20d='M12.851%2013.833c.119%200%20.238-.06.327-.149a.421.421%200%200%200%200-.653l-2.495-2.527%202.495-2.527a.421.421%200%200%200%200-.654.42.42%200%200%200-.654%200L10%209.82%207.476%207.323a.42.42%200%200%200-.653%200%20.42.42%200%200%200%200%20.654l2.494%202.527-2.494%202.527a.42.42%200%200%200%200%20.653c.089.09.208.15.327.15.118%200%20.237-.06.326-.15L10%2011.188l2.524%202.498c.09.089.208.148.327.148Z'%20fill='%23757575'%20stroke='%23757575'%20stroke-width='.6'/%3e%3c/svg%3e";
const container$3 = "_container_vtnbs_1";
const titleContainer = "_titleContainer_vtnbs_7";
const title$1 = "_title_vtnbs_7";
const clearAllButton = "_clearAllButton_vtnbs_21";
const recentSearchButton = "_recentSearchButton_vtnbs_22";
const deleteButton = "_deleteButton_vtnbs_23";
const buttonContainer = "_buttonContainer_vtnbs_38";
const icon$2 = "_icon_vtnbs_76";
const labelContainer$1 = "_labelContainer_vtnbs_83";
const label$1 = "_label_vtnbs_83";
const address$1 = "_address_vtnbs_93";
const logoContainer$1 = "_logoContainer_vtnbs_101";
const logo$1 = "_logo_vtnbs_101";
const styles$a = {
  container: container$3,
  titleContainer,
  title: title$1,
  clearAllButton,
  recentSearchButton,
  deleteButton,
  buttonContainer,
  icon: icon$2,
  labelContainer: labelContainer$1,
  label: label$1,
  address: address$1,
  logoContainer: logoContainer$1,
  logo: logo$1
};
function RecentSearchesList({
  title: title2,
  recentSearches,
  icon: icon2,
  recentSearchProps
}) {
  const {
    elementProps: { clearAllRecentSearchesButtonProps }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs("div", { className: styles$a["container"], children: [
    /* @__PURE__ */ jsxs("div", { className: styles$a["titleContainer"], children: [
      /* @__PURE__ */ jsx("p", { className: styles$a["title"], children: title2 }),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: styles$a["clearAllButton"],
          ...clearAllRecentSearchesButtonProps,
          children: "Clear All"
        }
      )
    ] }),
    recentSearches.map((node) => {
      const { containerProps, selectButtonProps, deleteButtonProps } = recentSearchProps(node);
      const { className: containerClassname, ...restContainerProps } = containerProps;
      const { className: deleteBtnClassname, ...restDeleteButtonProps } = deleteButtonProps;
      return /* @__PURE__ */ jsxs(
        "div",
        {
          role: "button",
          tabIndex: 0,
          className: clsx(containerClassname, styles$a["buttonContainer"]),
          ...restContainerProps,
          children: [
            /* @__PURE__ */ jsxs("button", { type: "button", className: styles$a["recentSearchButton"], ...selectButtonProps, children: [
              node.type === "navigable" ? /* @__PURE__ */ jsx("div", { className: styles$a["logoContainer"], children: /* @__PURE__ */ jsx(
                "img",
                {
                  src: node.packageId && node.packageId !== "Free" ? node.logo || placeholderLogo : placeholderLogo,
                  onError: (event) => {
                    event.currentTarget.src = placeholderLogo;
                  },
                  className: styles$a["logo"],
                  alt: `${node.label} logo`
                }
              ) }) : /* @__PURE__ */ jsx(Icon, { name: icon2, primary: true, large: true, className: styles$a["icon"] }),
              /* @__PURE__ */ jsxs("div", { className: styles$a["labelContainer"], children: [
                /* @__PURE__ */ jsx("p", { className: styles$a["label"], children: node.label }),
                !!("address" in node) && /* @__PURE__ */ jsx("p", { className: styles$a["address"], children: node.address })
              ] })
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: clsx(deleteBtnClassname, styles$a["deleteButton"]),
                ...restDeleteButtonProps,
                children: /* @__PURE__ */ jsx("img", { src: deleteIcon, alt: "Delete recent search" })
              }
            )
          ]
        },
        node.id
      );
    })
  ] });
}
const container$2 = "_container_g0uaz_1";
const title = "_title_g0uaz_7";
const button$1 = "_button_g0uaz_16";
const icon$1 = "_icon_g0uaz_36";
const label = "_label_g0uaz_43";
const suggestionListStyles = {
  container: container$2,
  title,
  button: button$1,
  icon: icon$1,
  label
};
function SuggestionList({
  title: title2,
  suggestions,
  renderSuggestion,
  icon: icon2,
  createButtonProps
}) {
  return /* @__PURE__ */ jsxs("div", { className: suggestionListStyles["container"], children: [
    !!title2 && /* @__PURE__ */ jsx("p", { className: suggestionListStyles["title"], children: title2 }),
    suggestions.map(
      renderSuggestion || ((node) => {
        var _a;
        const { className, ...buttonProps } = (createButtonProps == null ? void 0 : createButtonProps(node)) || {};
        return /* @__PURE__ */ jsxs(
          "div",
          {
            role: "button",
            className: clsx(className, suggestionListStyles["button"]),
            ...buttonProps,
            children: [
              /* @__PURE__ */ jsx(Icon, { name: icon2, primary: true, large: true, className: suggestionListStyles["icon"] }),
              /* @__PURE__ */ jsx("p", { className: suggestionListStyles["label"], children: ((_a = node.labelParts) == null ? void 0 : _a.length) ? node.labelParts.map((part) => part) : node.label })
            ]
          },
          node.id
        );
      })
    )
  ] });
}
function EmptyKeywordSuggestions() {
  const {
    elementProps: {
      createRecentSearchesButtonProps,
      createSuggestionButtonProps,
      createDeleteRecentSearchButtonProps
    },
    keywordSuggestions: { recentKeywordSearches, trendingSearches },
    ui: { showRecentKeywords }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showRecentKeywords && /* @__PURE__ */ jsx(
      RecentSearchesList,
      {
        title: "Recent Searches",
        recentSearches: recentKeywordSearches,
        icon: "clock-bold",
        recentSearchProps: (node) => ({
          containerProps: createSuggestionButtonProps(node, "keyword", node.type),
          selectButtonProps: createRecentSearchesButtonProps(node),
          deleteButtonProps: createDeleteRecentSearchButtonProps(node)
        })
      }
    ),
    /* @__PURE__ */ jsx(
      SuggestionList,
      {
        title: "Trending Searches",
        suggestions: trendingSearches.length ? trendingSearches : AUSTRALIA_TRENDING_SEARCHES,
        icon: "trendingUp-bold",
        createButtonProps: (node) => createSuggestionButtonProps(node, "keyword", "selectable", "trendingSearch")
      }
    )
  ] });
}
const text = "_text_ee21m_1";
const styles$9 = {
  text
};
function TextNotification({ children, className, ...props }) {
  return /* @__PURE__ */ jsx("p", { className: clsx(styles$9["text"], className), ...props, children });
}
function KeywordError() {
  const {
    elementProps: { createSuggestionButtonProps }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(TextNotification, { children: "Unable to fetch suggestions at the moment. Please refresh and try again." }),
    /* @__PURE__ */ jsx(
      SuggestionList,
      {
        title: "Trending Searches",
        suggestions: AUSTRALIA_TRENDING_SEARCHES,
        icon: "trendingUp-bold",
        createButtonProps: (node) => createSuggestionButtonProps(node, "keyword", "selectable")
      }
    )
  ] });
}
const loader = "data:image/gif;base64,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";
const container$1 = "_container_13upu_1";
const styles$8 = {
  container: container$1
};
const Loader = forwardRef(() => /* @__PURE__ */ jsx("div", { className: styles$8["container"], children: /* @__PURE__ */ jsx("img", { src: loader, height: "15px", width: "64px", alt: "Loader animation" }) }));
function LoadingKeywordSuggestions() {
  return /* @__PURE__ */ jsx(Loader, {});
}
const link = "_link_em8j4_1";
const styles$7 = {
  link
};
function TextLink({ children, ...props }) {
  return /* @__PURE__ */ jsx("a", { className: styles$7["link"], ...props, children });
}
function WithoutKeywordSuggestions() {
  const {
    elementProps: { createSuggestionButtonProps },
    keywordSuggestions: { trendingSearches }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(TextNotification, { children: [
      "Oops! No matches found. Try different keywords or",
      " ",
      /* @__PURE__ */ jsx(TextLink, { href: "https://business.localsearch.com.au/contact-us/", target: "_blank", children: "contact us" }),
      " ",
      "to suggest an industry."
    ] }),
    /* @__PURE__ */ jsx(
      SuggestionList,
      {
        title: "Trending Searches",
        suggestions: trendingSearches.length ? trendingSearches : AUSTRALIA_TRENDING_SEARCHES,
        icon: "trendingUp-bold",
        createButtonProps: (node) => createSuggestionButtonProps(node, "keyword", "selectable", "trendingSearch")
      }
    )
  ] });
}
const logoContainer = "_logoContainer_96uw6_1";
const logo = "_logo_96uw6_1";
const badgeContainer = "_badgeContainer_96uw6_18";
const badgePlatinum = "_badge-platinum_96uw6_32";
const badgeGold = "_badge-gold_96uw6_35";
const badgeSilver = "_badge-silver_96uw6_38";
const badgeBronze = "_badge-bronze_96uw6_41";
const badgeBlack = "_badge-black_96uw6_44";
const badge = "_badge_96uw6_18";
const labelContainer = "_labelContainer_96uw6_57";
const address = "_address_96uw6_61";
const styles$6 = {
  logoContainer,
  logo,
  badgeContainer,
  "badge-platinum": "_badge-platinum_96uw6_32",
  badgePlatinum,
  "badge-gold": "_badge-gold_96uw6_35",
  badgeGold,
  "badge-silver": "_badge-silver_96uw6_38",
  badgeSilver,
  "badge-bronze": "_badge-bronze_96uw6_41",
  badgeBronze,
  "badge-black": "_badge-black_96uw6_44",
  badgeBlack,
  badge,
  labelContainer,
  address
};
const BADGE_COLORS = {
  "1": "platinum",
  "2": "gold",
  "3": "silver",
  "4": "bronze",
  "5": "bronze",
  "9": "bronze",
  "10": "black",
  "14": null,
  Free: null
};
function WithKeywordSuggestions() {
  const {
    ui: { showCategorySuggestions, showProfileSuggestions },
    elementProps: { createSuggestionButtonProps },
    keywordSuggestions: { categorySuggestions, profileSuggestions }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showCategorySuggestions && /* @__PURE__ */ jsx(
      SuggestionList,
      {
        title: "Industries",
        suggestions: categorySuggestions,
        createButtonProps: (node) => createSuggestionButtonProps(node, "keyword", "selectable"),
        icon: "search-bold"
      }
    ),
    showProfileSuggestions && /* @__PURE__ */ jsx(
      SuggestionList,
      {
        title: "Businesses",
        suggestions: profileSuggestions,
        renderSuggestion: (node) => {
          var _a;
          const { className, ...buttonProps } = createSuggestionButtonProps(
            node,
            "keyword",
            "navigable"
          );
          const badgeColor = node.packageId ? BADGE_COLORS[node.packageId] : null;
          return /* @__PURE__ */ jsxs(
            "div",
            {
              role: "button",
              className: clsx(className, suggestionListStyles["button"]),
              ...buttonProps,
              children: [
                /* @__PURE__ */ jsxs("div", { className: styles$6["logoContainer"], children: [
                  /* @__PURE__ */ jsx(
                    "img",
                    {
                      src: node.packageId && node.packageId !== "Free" ? node.logo || placeholderLogo : placeholderLogo,
                      onError: (event) => {
                        event.currentTarget.src = placeholderLogo;
                      },
                      className: styles$6["logo"],
                      alt: `${node.label} logo`
                    }
                  ),
                  !!badgeColor && /* @__PURE__ */ jsx("div", { className: styles$6["badgeContainer"], children: /* @__PURE__ */ jsx(
                    Icon,
                    {
                      name: "badge-solid",
                      className: clsx(styles$6[`badge-${badgeColor}`], styles$6["badge"])
                    }
                  ) })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: styles$6["labelContainer"], children: [
                  /* @__PURE__ */ jsx("p", { className: suggestionListStyles["label"], children: ((_a = node.labelParts) == null ? void 0 : _a.length) ? node.labelParts.map((part) => part) : node.label }),
                  /* @__PURE__ */ jsx("p", { className: styles$6["address"], children: node.address })
                ] })
              ]
            },
            node.id
          );
        }
      }
    )
  ] });
}
function ResolvedKeywordSuggestions() {
  const {
    ui: { showWithKeywordSuggestions, showWithoutKeywordSuggestions }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showWithoutKeywordSuggestions && /* @__PURE__ */ jsx(WithoutKeywordSuggestions, {}),
    showWithKeywordSuggestions && /* @__PURE__ */ jsx(WithKeywordSuggestions, {})
  ] });
}
function KeywordFocused() {
  const {
    ui: {
      showEmptyKeywordSuggestions,
      showLoadingKeywordSuggestions,
      showResolvedKeywordSuggestions,
      showKeywordError
    }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showEmptyKeywordSuggestions && /* @__PURE__ */ jsx(EmptyKeywordSuggestions, {}),
    showLoadingKeywordSuggestions && /* @__PURE__ */ jsx(LoadingKeywordSuggestions, {}),
    showResolvedKeywordSuggestions && /* @__PURE__ */ jsx(ResolvedKeywordSuggestions, {}),
    showKeywordError && /* @__PURE__ */ jsx(KeywordError, {})
  ] });
}
function DisabledLocationPermissions() {
  return /* @__PURE__ */ jsxs(TextNotification, { children: [
    "We do not have permission to use your location.",
    " ",
    /* @__PURE__ */ jsx(
      TextLink,
      {
        href: "https://knowledge.localsearch.com.au/how-to-enable-location-permissions-on-localsearch-com-au/",
        target: "_blank",
        children: "Learn More"
      }
    )
  ] });
}
function InitialNearbyLocations() {
  const {
    elementProps: { useMyLocationButtonProps }
  } = useConnectContext();
  const { className, ...restUseMyLocationButtonProps } = useMyLocationButtonProps;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      className: clsx(className, suggestionListStyles["button"]),
      ...restUseMyLocationButtonProps,
      children: [
        /* @__PURE__ */ jsx(Icon, { name: "navigation-bold", primary: true, large: true, className: suggestionListStyles["icon"] }),
        /* @__PURE__ */ jsx("p", { className: suggestionListStyles["label"], children: "Use Current Location" })
      ]
    }
  );
}
function LoadingNearbyLocations() {
  return /* @__PURE__ */ jsx(Loader, {});
}
function ResolvedNearbyLocations() {
  const {
    elementProps: { createSuggestionButtonProps },
    locationSuggestions: { currentLocation, nearbyLocations },
    ui: { showCurrentLocation, showNearbyLocationSuggestions }
  } = useConnectContext();
  const { className: currentLocationClassName = "", ...currentLocationButtonProps } = currentLocation ? createSuggestionButtonProps(currentLocation, "location", "selectable") : {};
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showCurrentLocation && /* @__PURE__ */ jsxs(
      "div",
      {
        role: "button",
        className: clsx(currentLocationClassName, suggestionListStyles["button"]),
        ...currentLocationButtonProps,
        children: [
          /* @__PURE__ */ jsx(Icon, { name: "navigation-bold", primary: true, className: suggestionListStyles["icon"] }),
          /* @__PURE__ */ jsxs("p", { className: suggestionListStyles["label"], children: [
            currentLocation == null ? void 0 : currentLocation.shortLabel,
            " - Current Location"
          ] })
        ]
      }
    ),
    showNearbyLocationSuggestions && /* @__PURE__ */ jsx(
      SuggestionList,
      {
        title: "Nearby Locations",
        suggestions: nearbyLocations,
        icon: "trendingUp-bold",
        createButtonProps: (node) => createSuggestionButtonProps(node, "location", "selectable")
      }
    )
  ] });
}
function NearbyLocations() {
  const {
    ui: {
      showInitialNearbyLocations,
      showLoadingNearbyLocations,
      showResolvedNearbyLocations,
      showDisabledNearbyLocations
    }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showLoadingNearbyLocations && /* @__PURE__ */ jsx(LoadingNearbyLocations, {}),
    showInitialNearbyLocations && /* @__PURE__ */ jsx(InitialNearbyLocations, {}),
    showResolvedNearbyLocations && /* @__PURE__ */ jsx(ResolvedNearbyLocations, {}),
    showDisabledNearbyLocations && /* @__PURE__ */ jsx(DisabledLocationPermissions, {})
  ] });
}
function EmptyLocationSuggestions() {
  const {
    ui: { showRecentLocations },
    locationSuggestions: { recentLocationSearches },
    elementProps: {
      createSuggestionButtonProps,
      createRecentSearchesButtonProps,
      createDeleteRecentSearchButtonProps
    }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(NearbyLocations, {}),
    showRecentLocations && /* @__PURE__ */ jsx(
      RecentSearchesList,
      {
        title: "Recent Locations",
        recentSearches: recentLocationSearches,
        icon: "clock-bold",
        recentSearchProps: (node) => ({
          containerProps: createSuggestionButtonProps(node, "location", node.type),
          selectButtonProps: createRecentSearchesButtonProps(node),
          deleteButtonProps: createDeleteRecentSearchButtonProps(node)
        })
      }
    )
  ] });
}
function LocationError() {
  return /* @__PURE__ */ jsx(TextNotification, { children: "Unable to fetch suggestions at the moment. Please refresh and try again." });
}
function LoadingLocationSuggestions() {
  return /* @__PURE__ */ jsx(Loader, {});
}
function WithMultipleLocationSuggestions() {
  const {
    elementProps: { createSuggestionButtonProps },
    locationSuggestions: { searchAreaSuggestions }
  } = useConnectContext();
  return /* @__PURE__ */ jsx(
    SuggestionList,
    {
      suggestions: searchAreaSuggestions,
      icon: "search-bold",
      createButtonProps: (node) => createSuggestionButtonProps(node, "location", "selectable")
    }
  );
}
function WithOneLocationSuggestion() {
  const {
    elementProps: { createSuggestionButtonProps },
    locationSuggestions: { searchAreaSuggestions }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      SuggestionList,
      {
        suggestions: searchAreaSuggestions,
        icon: "search-bold",
        createButtonProps: (node) => createSuggestionButtonProps(node, "location", "selectable")
      }
    ),
    /* @__PURE__ */ jsx(TextNotification, { children: "If your area is not listed, try searching in our coverage states." }),
    /* @__PURE__ */ jsx(
      SuggestionList,
      {
        title: "Our Coverage States",
        suggestions: COVERAGE_STATES,
        icon: "externalLink-bold",
        createButtonProps: (node) => createSuggestionButtonProps(node, "location", "navigable")
      }
    )
  ] });
}
function WithoutLocationSuggestions() {
  const {
    elementProps: { createSuggestionButtonProps }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(TextNotification, { children: "Oops! We don't cover your area yet. Try searching for statewide services instead, and use our map view to find what you're looking for." }),
    /* @__PURE__ */ jsx(
      SuggestionList,
      {
        title: "Our Coverage States",
        suggestions: COVERAGE_STATES,
        icon: "externalLink-bold",
        createButtonProps: (node) => createSuggestionButtonProps(node, "location", "navigable")
      }
    )
  ] });
}
function ResolvedLocationSuggestions() {
  const {
    ui: {
      showWithoutLocationSuggestions,
      showWithOneLocationSuggestion,
      showWithMultipleLocationSuggestions
    }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showWithoutLocationSuggestions && /* @__PURE__ */ jsx(WithoutLocationSuggestions, {}),
    showWithOneLocationSuggestion && /* @__PURE__ */ jsx(WithOneLocationSuggestion, {}),
    showWithMultipleLocationSuggestions && /* @__PURE__ */ jsx(WithMultipleLocationSuggestions, {})
  ] });
}
function LocationFocused() {
  const {
    ui: {
      showEmptyLocationSuggestions,
      showLoadingLocationSuggestions,
      showResolvedLocationSuggestions,
      showLocationError
    }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showEmptyLocationSuggestions && /* @__PURE__ */ jsx(EmptyLocationSuggestions, {}),
    showLoadingLocationSuggestions && /* @__PURE__ */ jsx(LoadingLocationSuggestions, {}),
    showResolvedLocationSuggestions && /* @__PURE__ */ jsx(ResolvedLocationSuggestions, {}),
    showLocationError && /* @__PURE__ */ jsx(LocationError, {})
  ] });
}
const dialog$4 = "_dialog_1t1f1_22";
const dialogOpen = "_dialogOpen_1t1f1_34";
const slideUp = "_slideUp_1t1f1_1";
const dialogClose = "_dialogClose_1t1f1_37";
const slideDown = "_slideDown_1t1f1_1";
const closeDialogBtn = "_closeDialogBtn_1t1f1_41";
const inputContainer$1 = "_inputContainer_1t1f1_47";
const locationInputContainer = "_locationInputContainer_1t1f1_59";
const inputField$1 = "_inputField_1t1f1_64";
const inputIcon$2 = "_inputIcon_1t1f1_92";
const suggestionsContainer = "_suggestionsContainer_1t1f1_98";
const styles$5 = {
  dialog: dialog$4,
  dialogOpen,
  slideUp,
  dialogClose,
  slideDown,
  closeDialogBtn,
  inputContainer: inputContainer$1,
  locationInputContainer,
  inputField: inputField$1,
  inputIcon: inputIcon$2,
  suggestionsContainer
};
function MobileDialog({ keywordInputRef, locationInputRef, ...props }) {
  const isSsr = useIsSsr();
  const dialogRef = useRef(null);
  const [animateDialog, setAnimateDialog] = useState("idle");
  const defaultKeywordRef = useRef(null);
  const defaultLocationRef = useRef(null);
  const keywordRef = keywordInputRef || defaultKeywordRef;
  const locationRef = locationInputRef || defaultLocationRef;
  const {
    ui: { showDialog, showKeywordSuggestions, showLocationSuggestions },
    elementProps: { closeMobileDialogProps, createKeywordInputProps, createLocationInputProps }
  } = useConnectContext();
  const createPortal$1 = useCallback(
    (render) => {
      if (isSsr) return null;
      return createPortal(render, document.body);
    },
    [isSsr]
  );
  useEffect(() => clearAllBodyScrollLocks, []);
  useEffect(() => {
    var _a, _b;
    if (!dialogRef.current) {
      setAnimateDialog("idle");
      return;
    }
    if (showDialog) {
      setAnimateDialog("open");
      dialogRef.current.show();
      disableBodyScroll(dialogRef.current);
      if (showKeywordSuggestions) {
        (_a = keywordRef.current) == null ? void 0 : _a.focus();
      } else if (showLocationSuggestions) {
        (_b = locationRef.current) == null ? void 0 : _b.focus();
      }
    } else {
      setAnimateDialog("close");
      setTimeout(() => {
        dialogRef.current.close();
        enableBodyScroll(dialogRef.current);
        setAnimateDialog("idle");
      }, 300);
    }
  }, [showDialog]);
  return createPortal$1(
    /* @__PURE__ */ jsxs(
      Dialog,
      {
        className: clsx(
          "sb-searchbarRoot",
          styles$5["dialog"],
          animateDialog === "open" && styles$5["dialogOpen"],
          animateDialog === "close" && styles$5["dialogClose"]
        ),
        ...props,
        ref: dialogRef,
        children: [
          /* @__PURE__ */ jsx("button", { type: "button", className: styles$5["closeDialogBtn"], ...closeMobileDialogProps, children: /* @__PURE__ */ jsx(Icon, { name: "x-bold", black: true }) }),
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: styles$5["inputContainer"],
              onClick: () => {
                var _a;
                return (_a = keywordRef.current) == null ? void 0 : _a.focus();
              },
              children: [
                /* @__PURE__ */ jsx(Icon, { name: "search-bold", primary: showKeywordSuggestions, className: styles$5["inputIcon"] }),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    className: clsx(styles$5["inputField"]),
                    placeholder: MOBILE_KEYWORD_PLACEHOLDER,
                    ...createKeywordInputProps(true),
                    ref: keywordRef
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: clsx(styles$5["inputContainer"], styles$5["locationInputContainer"]),
              onClick: () => {
                var _a;
                return (_a = locationRef.current) == null ? void 0 : _a.focus();
              },
              children: [
                /* @__PURE__ */ jsx(
                  Icon,
                  {
                    name: "mapPin-bold",
                    primary: showLocationSuggestions,
                    className: styles$5["inputIcon"]
                  }
                ),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    className: clsx(styles$5["inputField"]),
                    placeholder: MOBILE_LOCATION_PLACEHOLDER,
                    ...createLocationInputProps(true),
                    ref: locationRef
                  }
                )
              ]
            }
          ),
          !!showKeywordSuggestions && /* @__PURE__ */ jsx(KeywordFocused, {}),
          !!showLocationSuggestions && /* @__PURE__ */ jsx(LocationFocused, {})
        ]
      }
    )
  );
}
const searchButtonLoader = "data:image/gif;base64,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";
function useOutsideClick(onOutsideClick) {
  const ref = useRef(null);
  useEffect(() => {
    function listener(event) {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      onOutsideClick(event);
    }
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [onOutsideClick]);
  return ref;
}
function handleMouseMove(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  const xpercent = x / e.currentTarget.offsetWidth;
  const ypercent = y / e.currentTarget.offsetHeight;
  e.currentTarget.style.setProperty("--mouse-x", String(xpercent * 100));
  e.currentTarget.style.setProperty("--mouse-y", String(ypercent * 100));
}
const searchbarContainer$2 = "_searchbarContainer_kk6uv_1";
const inputContainer = "_inputContainer_kk6uv_8";
const keywordContainer = "_keywordContainer_kk6uv_21";
const divider$2 = "_divider_kk6uv_26";
const showDivider = "_showDivider_kk6uv_39";
const locationContainer = "_locationContainer_kk6uv_43";
const unfocusedInput = "_unfocusedInput_kk6uv_48";
const inputField = "_inputField_kk6uv_52";
const inputIcon$1 = "_inputIcon_kk6uv_81";
const searchButton = "_searchButton_kk6uv_91";
const autocomplete = "_autocomplete_kk6uv_155";
const locationAutocomplete = "_locationAutocomplete_kk6uv_175";
const keywordAutocomplete = "_keywordAutocomplete_kk6uv_179";
const dialog$3 = "_dialog_kk6uv_183";
const styles$4 = {
  searchbarContainer: searchbarContainer$2,
  inputContainer,
  keywordContainer,
  divider: divider$2,
  showDivider,
  locationContainer,
  unfocusedInput,
  inputField,
  inputIcon: inputIcon$1,
  searchButton,
  autocomplete,
  locationAutocomplete,
  keywordAutocomplete,
  dialog: dialog$3
};
function PageSearchDesktop({
  keywordInputRef,
  locationInputRef,
  autofocusLocation = false,
  autofocusKeyword = false
}) {
  const keywordInputRefDefault = useRef(null);
  const locationInputRefDefault = useRef(null);
  const keywordRef = keywordInputRef || keywordInputRefDefault;
  const locationRef = locationInputRef || locationInputRefDefault;
  const {
    elementProps: {
      outsideClickHandlerProps,
      createKeywordInputProps,
      createLocationInputProps,
      searchButtonProps
    },
    ui: {
      showKeywordSuggestions,
      showLocationSuggestions,
      showKeywordAutocomplete,
      showLocationAutocomplete,
      showSearchButtonLoading
    },
    keywordSuggestions: { keywordAutocomplete: keywordAutocomplete2 },
    locationSuggestions: { locationAutocomplete: locationAutocomplete2 }
  } = useConnectContext();
  const containerRef = useOutsideClick(outsideClickHandlerProps.handleOutsideClick);
  useEffect(() => {
    if (autofocusLocation && locationRef.current) {
      setTimeout(() => {
        var _a;
        (_a = locationRef.current) == null ? void 0 : _a.focus();
      }, 0);
    }
  }, [autofocusLocation, locationRef.current]);
  useEffect(() => {
    if (autofocusKeyword && keywordRef.current) {
      setTimeout(() => {
        var _a;
        (_a = keywordRef.current) == null ? void 0 : _a.focus();
      }, 0);
    }
  }, [autofocusKeyword, keywordRef.current]);
  return /* @__PURE__ */ jsxs("div", { className: styles$4["searchbarContainer"], ref: containerRef, children: [
    /* @__PURE__ */ jsxs(
      "div",
      {
        role: "button",
        tabIndex: 0,
        onKeyDown: () => {
        },
        className: clsx(
          styles$4["inputContainer"],
          styles$4["keywordContainer"],
          showLocationSuggestions && styles$4["unfocusedInput"]
        ),
        onClick: () => {
          var _a;
          return (_a = keywordRef.current) == null ? void 0 : _a.focus();
        },
        children: [
          /* @__PURE__ */ jsx(Icon, { name: "search-bold", primary: showKeywordSuggestions, className: styles$4["inputIcon"] }),
          /* @__PURE__ */ jsx(
            "input",
            {
              className: styles$4["inputField"],
              ...createKeywordInputProps(),
              placeholder: "Business or Industry",
              ref: keywordRef,
              name: "search-keyword-input",
              autoComplete: "off",
              autoCapitalize: "off",
              autoCorrect: "off"
            }
          ),
          showKeywordAutocomplete && /* @__PURE__ */ jsxs("p", { className: clsx(styles$4["autocomplete"], styles$4["keywordAutocomplete"]), children: [
            /* @__PURE__ */ jsx("span", { className: styles$4["autocompleteInput"], children: keywordAutocomplete2.input }),
            keywordAutocomplete2.autocomplete
          ] }),
          /* @__PURE__ */ jsx(Dialog, { open: showKeywordSuggestions, className: styles$4["dialog"], children: /* @__PURE__ */ jsx(KeywordFocused, {}) })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: clsx(
          styles$4["divider"],
          !showKeywordSuggestions && !showLocationSuggestions && styles$4["showDivider"]
        )
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        role: "button",
        tabIndex: 0,
        onKeyDown: () => {
          var _a;
          return (_a = locationRef.current) == null ? void 0 : _a.focus();
        },
        className: clsx(
          styles$4["inputContainer"],
          styles$4["locationContainer"],
          showKeywordSuggestions && styles$4["unfocusedInput"]
        ),
        onClick: () => {
          var _a;
          return (_a = locationRef.current) == null ? void 0 : _a.focus();
        },
        children: [
          /* @__PURE__ */ jsx(
            Icon,
            {
              name: "mapPin-bold",
              primary: showLocationSuggestions,
              className: styles$4["inputIcon"]
            }
          ),
          /* @__PURE__ */ jsx(
            "input",
            {
              className: styles$4["inputField"],
              ...createLocationInputProps(),
              placeholder: "Location",
              ref: locationRef,
              name: "search-location-input",
              autoComplete: "off",
              autoCapitalize: "off",
              autoCorrect: "off"
            }
          ),
          showLocationAutocomplete && /* @__PURE__ */ jsxs("p", { className: clsx(styles$4["autocomplete"], styles$4["locationAutocomplete"]), children: [
            /* @__PURE__ */ jsx("span", { children: locationAutocomplete2.input }),
            locationAutocomplete2.autocomplete
          ] }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: styles$4["searchButton"],
              onMouseMove: handleMouseMove,
              ...searchButtonProps,
              children: showSearchButtonLoading ? /* @__PURE__ */ jsx("img", { alt: "Loading search results animation", src: searchButtonLoader, width: 50.63 }) : "Search"
            }
          ),
          /* @__PURE__ */ jsx(Dialog, { open: showLocationSuggestions, className: styles$4["dialog"], children: /* @__PURE__ */ jsx(LocationFocused, {}) })
        ]
      }
    )
  ] });
}
const button = "_button_1lfch_1";
const dialog$2 = "_dialog_1lfch_6";
const style = {
  button,
  dialog: dialog$2
};
function IconSearch({ media = "mobile" }) {
  const dialogRef = useRef(null);
  const {
    elementProps: { searchIconButtonProps },
    ui: { showDialog }
  } = useConnectContext();
  useEffect(() => clearAllBodyScrollLocks, []);
  useEffect(() => {
    if (!dialogRef.current) {
      return;
    }
    if (showDialog) {
      disableBodyScroll(dialogRef.current);
      dialogRef.current.showModal();
    } else {
      enableBodyScroll(dialogRef.current);
      dialogRef.current.close();
    }
  }, [showDialog]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("button", { type: "button", ...searchIconButtonProps, className: style["button"], children: /* @__PURE__ */ jsx(Icon, { name: "search-bold", black: true }) }),
    media === "mobile" && /* @__PURE__ */ jsx(MobileDialog, {}),
    media === "desktop" && /* @__PURE__ */ jsx("dialog", { className: style["dialog"], ref: dialogRef, children: /* @__PURE__ */ jsx(PageSearchDesktop, {}) })
  ] });
}
const searchbarContainer$1 = "_searchbarContainer_p2yh0_1";
const inputBtn$2 = "_inputBtn_p2yh0_10";
const keywordInputBtn$1 = "_keywordInputBtn_p2yh0_16";
const locationInputBtn$1 = "_locationInputBtn_p2yh0_20";
const icon = "_icon_p2yh0_25";
const inputText$2 = "_inputText_p2yh0_33";
const placeholder$2 = "_placeholder_p2yh0_42";
const divider$1 = "_divider_p2yh0_46";
const dialog$1 = "_dialog_p2yh0_52";
const dialogContainer$1 = "_dialogContainer_p2yh0_69";
const styles$3 = {
  searchbarContainer: searchbarContainer$1,
  inputBtn: inputBtn$2,
  keywordInputBtn: keywordInputBtn$1,
  locationInputBtn: locationInputBtn$1,
  icon,
  inputText: inputText$2,
  placeholder: placeholder$2,
  divider: divider$1,
  dialog: dialog$1,
  dialogContainer: dialogContainer$1
};
function NavbarSearchDesktop() {
  var _a, _b, _c, _d;
  const dialogRef = useRef(null);
  const keywordInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const {
    elementProps: { keywordInputButtonProps, locationInputButtonProps },
    ui: { showDialog, showLocationSuggestions, showKeywordSuggestions }
  } = useConnectContext();
  useEffect(() => clearAllBodyScrollLocks, []);
  useEffect(() => {
    if (!dialogRef.current) {
      return;
    }
    if (showDialog) {
      disableBodyScroll(dialogRef.current);
      dialogRef.current.showModal();
    } else {
      enableBodyScroll(dialogRef.current);
      dialogRef.current.close();
    }
  }, [showDialog]);
  return /* @__PURE__ */ jsxs("div", { className: styles$3["searchbarContainer"], children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        className: clsx(styles$3["inputBtn"], styles$3["keywordInputBtn"]),
        ...keywordInputButtonProps,
        children: [
          /* @__PURE__ */ jsx(Icon, { name: "search-bold", className: styles$3["icon"] }),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: clsx(
                styles$3["inputText"],
                !((_a = keywordInputRef.current) == null ? void 0 : _a.value) && styles$3["placeholder"]
              ),
              children: ((_b = keywordInputRef.current) == null ? void 0 : _b.value) || "Business/Industry"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { className: styles$3["divider"] }),
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        className: clsx(styles$3["inputBtn"], styles$3["locationInputBtn"]),
        ...locationInputButtonProps,
        children: [
          /* @__PURE__ */ jsx(Icon, { name: "mapPin-bold", className: styles$3["icon"] }),
          /* @__PURE__ */ jsx(
            "p",
            {
              className: clsx(
                styles$3["inputText"],
                !((_c = locationInputRef.current) == null ? void 0 : _c.value) && styles$3["placeholder"]
              ),
              children: ((_d = locationInputRef.current) == null ? void 0 : _d.value) || "Location"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx("dialog", { ref: dialogRef, className: styles$3["dialog"], children: /* @__PURE__ */ jsx("div", { className: styles$3["dialogContainer"], children: /* @__PURE__ */ jsx(
      PageSearchDesktop,
      {
        keywordInputRef,
        locationInputRef,
        autofocusKeyword: showKeywordSuggestions,
        autofocusLocation: showLocationSuggestions
      }
    ) }) })
  ] });
}
const searchbarContainer = "_searchbarContainer_v8pni_1";
const inputBtn$1 = "_inputBtn_v8pni_11";
const keywordInputBtn = "_keywordInputBtn_v8pni_16";
const locationInputBtn = "_locationInputBtn_v8pni_21";
const iconBtn = "_iconBtn_v8pni_27";
const inputText$1 = "_inputText_v8pni_33";
const placeholder$1 = "_placeholder_v8pni_42";
const divider = "_divider_v8pni_46";
const styles$2 = {
  searchbarContainer,
  inputBtn: inputBtn$1,
  keywordInputBtn,
  locationInputBtn,
  iconBtn,
  inputText: inputText$1,
  placeholder: placeholder$1,
  divider
};
function NavbarSearchMobile() {
  var _a, _b, _c, _d;
  const keywordInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const {
    elementProps: { keywordInputButtonProps, locationInputButtonProps }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs("div", { className: styles$2["searchbarContainer"], children: [
    /* @__PURE__ */ jsx("button", { type: "button", className: styles$2["iconBtn"], ...keywordInputButtonProps, children: /* @__PURE__ */ jsx(Icon, { name: "search-bold", primary: true }) }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: clsx(styles$2["inputBtn"], styles$2["keywordInputBtn"]),
        ...keywordInputButtonProps,
        children: /* @__PURE__ */ jsx(
          "p",
          {
            className: clsx(
              styles$2["inputText"],
              !((_a = keywordInputRef.current) == null ? void 0 : _a.value) && styles$2["placeholder"]
            ),
            children: ((_b = keywordInputRef.current) == null ? void 0 : _b.value) || "Business/Industry"
          }
        )
      }
    ),
    /* @__PURE__ */ jsx("div", { className: styles$2["divider"] }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: clsx(styles$2["inputBtn"], styles$2["locationInputBtn"]),
        ...locationInputButtonProps,
        children: /* @__PURE__ */ jsx(
          "p",
          {
            className: clsx(
              styles$2["inputText"],
              !((_c = locationInputRef.current) == null ? void 0 : _c.value) && styles$2["placeholder"]
            ),
            children: ((_d = locationInputRef.current) == null ? void 0 : _d.value) || "Location"
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(MobileDialog, { keywordInputRef, locationInputRef })
  ] });
}
function NavbarSearch({ media }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    media === "mobile" && /* @__PURE__ */ jsx(NavbarSearchMobile, {}),
    media === "desktop" && /* @__PURE__ */ jsx(NavbarSearchDesktop, {})
  ] });
}
const container = "_container_1r6nn_1";
const inputBtn = "_inputBtn_1r6nn_9";
const inputBtnContainer = "_inputBtnContainer_1r6nn_16";
const inputIcon = "_inputIcon_1r6nn_26";
const inputText = "_inputText_1r6nn_32";
const placeholder = "_placeholder_1r6nn_41";
const searchBtn = "_searchBtn_1r6nn_45";
const styles$1 = {
  container,
  inputBtn,
  inputBtnContainer,
  inputIcon,
  inputText,
  placeholder,
  searchBtn
};
function PageSearchMobile() {
  var _a, _b, _c, _d;
  const keywordInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const {
    elementProps: { keywordInputButtonProps, locationInputButtonProps, searchButtonProps }
  } = useConnectContext();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: styles$1["container"], children: [
      /* @__PURE__ */ jsx("button", { type: "button", className: styles$1["inputBtn"], ...keywordInputButtonProps, children: /* @__PURE__ */ jsxs("div", { className: styles$1["inputBtnContainer"], children: [
        /* @__PURE__ */ jsx(Icon, { name: "search-bold", className: styles$1["inputIcon"] }),
        /* @__PURE__ */ jsx(
          "p",
          {
            className: clsx(
              styles$1["inputText"],
              !((_a = keywordInputRef.current) == null ? void 0 : _a.value) && styles$1["placeholder"]
            ),
            children: ((_b = keywordInputRef.current) == null ? void 0 : _b.value) || MOBILE_KEYWORD_PLACEHOLDER
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx("button", { type: "button", className: styles$1["inputBtn"], ...locationInputButtonProps, children: /* @__PURE__ */ jsxs("div", { className: styles$1["inputBtnContainer"], children: [
        /* @__PURE__ */ jsx(Icon, { name: "mapPin-bold", className: styles$1["inputIcon"] }),
        /* @__PURE__ */ jsx(
          "p",
          {
            className: clsx(
              styles$1["inputText"],
              !((_c = locationInputRef.current) == null ? void 0 : _c.value) && styles$1["placeholder"]
            ),
            children: ((_d = locationInputRef.current) == null ? void 0 : _d.value) || MOBILE_LOCATION_PLACEHOLDER
          }
        )
      ] }) }),
      /* @__PURE__ */ jsx("button", { type: "button", className: styles$1["searchBtn"], ...searchButtonProps, children: "Search" })
    ] }),
    /* @__PURE__ */ jsx(MobileDialog, { keywordInputRef, locationInputRef })
  ] });
}
function PageSearch({ media }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    media === "mobile" && /* @__PURE__ */ jsx(PageSearchMobile, {}),
    media === "desktop" && /* @__PURE__ */ jsx(PageSearchDesktop, {})
  ] });
}
const dialog = "_dialog_xbp2u_1";
const dialogContainer = "_dialogContainer_xbp2u_15";
const styles = {
  dialog,
  dialogContainer
};
function SearchDialog({ media, keywordInputRef, locationInputRef }) {
  const dialogRef = useRef(null);
  const {
    ui: { showDialog, showLocationSuggestions }
  } = useConnectContext();
  const defaultKeywordRef = useRef(null);
  const defaultLocationRef = useRef(null);
  const keywordRef = keywordInputRef || defaultKeywordRef;
  const locationRef = locationInputRef || defaultLocationRef;
  useEffect(() => {
    if (!dialogRef.current) {
      return;
    }
    if (showDialog) {
      disableBodyScroll(dialogRef.current);
      dialogRef.current.showModal();
    } else {
      enableBodyScroll(dialogRef.current);
      dialogRef.current.close();
    }
  }, [showDialog]);
  useEffect(() => clearAllBodyScrollLocks, []);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    media === "mobile" && /* @__PURE__ */ jsx(MobileDialog, { keywordInputRef: keywordRef, locationInputRef: locationRef }),
    media === "desktop" && /* @__PURE__ */ jsx("dialog", { ref: dialogRef, className: styles["dialog"], children: /* @__PURE__ */ jsx("div", { className: styles["dialogContainer"], children: /* @__PURE__ */ jsx(
      PageSearchDesktop,
      {
        keywordInputRef: keywordRef,
        locationInputRef: locationRef,
        autofocusLocation: showLocationSuggestions
      }
    ) }) })
  ] });
}
const Search = forwardRef(
  ({
    id = "searchBar",
    type = "navbar",
    media = "mobile",
    withoutProvider = false,
    initialCategory,
    initialProfile,
    initialSearchArea,
    pageDataLayerValues,
    onNavigate,
    keywordInputRef,
    locationInputRef,
    ...props
  }, ref) => {
    const SearchComponent = /* @__PURE__ */ jsxs("div", { className: "sb-searchbarRoot", ...props, ref, children: [
      type === "navbar" && /* @__PURE__ */ jsx(NavbarSearch, { media }),
      type === "page" && /* @__PURE__ */ jsx(PageSearch, { media }),
      type === "icon" && /* @__PURE__ */ jsx(IconSearch, { media }),
      type === "dialog" && /* @__PURE__ */ jsx(
        SearchDialog,
        {
          media,
          keywordInputRef,
          locationInputRef
        }
      )
    ] });
    return withoutProvider ? SearchComponent : /* @__PURE__ */ jsx(
      SearchConnectProvider,
      {
        id,
        initialCategory,
        initialProfile,
        initialSearchArea,
        pageDataLayerValues,
        onNavigate,
        children: SearchComponent
      }
    );
  }
);
export {
  SearchConnectProvider,
  Search as default,
  useSearchContext
};
